import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';

// ─────────────────────────────────────────────────────────────────────────
// THE TECHNIQUE LIBRARY
// Window Washing Guide — Tool 04 of 09
// ─────────────────────────────────────────────────────────────────────────
//
// The canonical destination for animated technique reference. Each
// chapter is a self-contained motion study — the squeegee strokes, the
// detail work, the application pass, eventually the razor scrape and
// the water-fed pole. All share the same animation engine and design
// system; chapters add via a single config object.
//
// Architecture:
//   • CHAPTERS array — the source of truth. Each chapter has metadata
//     (name, italic, rating, description, notes, diagnostic) plus an
//     animation spec (sub-strokes, path functions, pane setup).
//   • One unified canvas component renders any chapter's animation.
//   • Adding a chapter = adding to the array. No new components.
//
// Chapters live now:
//   01. The Squeegee Strokes (fan, straight, S)
//   02. The Detail Stroke (L-perimeter, corner pinch, lap-and-lift)
//   03. The Razor Scrape (low-angle, angle test, edge approach)
//   04. The Application Pass (sleeve wash, sea sponge dab, spray-and-spread)
//   05. The Channel Wipe (cross-body, thigh, bucket-edge)
//   06. The Bucket Setup (two-bucket, sleeve loading, tool layout)
//   07. The Tilt-In Reach (sash tilt-in, reach-across, re-seat)
//   08. The Pole Technique (low-angle pole, two-story sweep, brush-and-rinse)
//
// ─────────────────────────────────────────────────────────────────────────

// Pane dimensions in SVG viewBox units
const W = 600;
const H = 420;
const M = 24;

// ═════════════════════════════════════════════════════════════════════════
// ANIMATION ENGINE — path functions + tool glyphs
// ═════════════════════════════════════════════════════════════════════════

// ─── Path: serpentine fan ────────────────────────────────────────────────
function pathFan(t) {
  const left = M, right = W - M, top = M, bot = H - M;
  const arcs = 3;
  const totalY = bot - top;
  const phase = t * arcs;
  const i = Math.min(Math.floor(phase), arcs - 1);
  const local = phase - i;
  const yStart = top + (i / arcs) * totalY;
  const yEnd = top + ((i + 1) / arcs) * totalY;
  const y = yStart + local * (yEnd - yStart);
  const goingRight = i % 2 === 0;
  const xStart = goingRight ? left : right;
  const xEnd = goingRight ? right : left;
  const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
  const x = xStart + eased * (xEnd - xStart);
  const baseAngle = goingRight ? -25 : 25;
  const turnAngle = goingRight ? 25 : -25;
  const angle = baseAngle + (turnAngle - baseAngle) * eased;
  return { x, y, angle, tool: 'squeegee' };
}

// ─── Path: straight horizontal pulls with lift between ───────────────────
function pathStraight(t) {
  const left = M, right = W - M, top = M + 10, bot = H - M;
  const passes = 5;
  const passHeight = (bot - top) / (passes - 1);
  const phase = t * passes;
  const i = Math.min(Math.floor(phase), passes - 1);
  const local = phase - i;
  const y = top + i * passHeight;
  if (local < 0.7) {
    const strokeT = local / 0.7;
    return { x: left + strokeT * (right - left), y, angle: 0, lifted: false, tool: 'squeegee' };
  } else {
    const liftT = (local - 0.7) / 0.3;
    const x = right - liftT * (right - left);
    const lift = Math.sin(liftT * Math.PI) * 14;
    return { x, y: y - lift, angle: 0, lifted: true, tool: 'squeegee' };
  }
}

// ─── Path: S-curve ──────────────────────────────────────────────────────
function pathS(t) {
  const left = M, right = W - M, top = M, bot = H - M;
  if (t < 0.4) {
    const local = t / 0.4;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: left + eased * (right - left),
      y: top + local * ((bot - top) * 0.4),
      angle: -20 + 40 * eased,
      tool: 'squeegee',
    };
  } else {
    const local = (t - 0.4) / 0.6;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: right - eased * (right - left),
      y: top + (bot - top) * 0.4 + local * ((bot - top) * 0.6),
      angle: 20 - 40 * eased,
      tool: 'squeegee',
    };
  }
}

// ─── Path: L-perimeter (detail stroke around the edges) ──────────────────
function pathLPerimeter(t) {
  const inset = 8;
  const left = M + inset, right = W - M - inset;
  const top = M + inset, bot = H - M - inset;
  // Path: top-left → right along top → down right side → left along bottom → up left side
  const perimLen = 2 * (right - left) + 2 * (bot - top);
  const d = t * perimLen;
  let x, y, angle;
  let segment = 0;
  if (d < right - left) {
    // top edge, left → right
    x = left + d;
    y = top;
    angle = 0;
    segment = 0;
  } else if (d < (right - left) + (bot - top)) {
    // right edge, top → bottom
    x = right;
    y = top + (d - (right - left));
    angle = 90;
    segment = 1;
  } else if (d < 2 * (right - left) + (bot - top)) {
    // bottom edge, right → left
    x = right - (d - ((right - left) + (bot - top)));
    y = bot;
    angle = 180;
    segment = 2;
  } else {
    // left edge, bottom → top
    x = left;
    y = bot - (d - (2 * (right - left) + (bot - top)));
    angle = 270;
    segment = 3;
  }
  return { x, y, angle, tool: 'cloth', segment };
}

// ─── Path: corner pinch (slow zoom on a corner, cloth folding in) ────────
// Stays in one corner, demonstrates the pinch motion that gets dirt out
// of the 90° angle. Uses a tighter arc that pushes into the corner.
function pathCornerPinch(t) {
  // Two cycles: approach corner from top, pinch, withdraw, approach from
  // side, pinch, withdraw.
  const cornerX = M + 36;
  const cornerY = M + 36;
  const cycle = (t * 2) % 1;
  const phase = Math.floor(t * 2);

  // Approach direction varies by phase
  const approachAngle = phase === 0 ? 0 : 90;
  const approachFromX = phase === 0 ? cornerX + 80 : cornerX;
  const approachFromY = phase === 0 ? cornerY : cornerY + 80;

  // Three sub-phases per cycle: approach (0..0.4), pinch (0.4..0.6), retreat (0.6..1)
  let x, y, scale;
  if (cycle < 0.4) {
    const local = cycle / 0.4;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    x = approachFromX + (cornerX - approachFromX) * eased;
    y = approachFromY + (cornerY - approachFromY) * eased;
    scale = 1;
  } else if (cycle < 0.6) {
    // Pinch — slight wiggle into the corner
    const local = (cycle - 0.4) / 0.2;
    x = cornerX + Math.sin(local * Math.PI * 4) * 2;
    y = cornerY + Math.sin(local * Math.PI * 4) * 2;
    scale = 1 - 0.1 * Math.sin(local * Math.PI);
  } else {
    const local = (cycle - 0.6) / 0.4;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    x = cornerX + (approachFromX - cornerX) * eased;
    y = cornerY + (approachFromY - cornerY) * eased;
    scale = 1;
  }
  return { x, y, angle: approachAngle, tool: 'cloth', scale, isPinch: cycle >= 0.4 && cycle < 0.6 };
}

// ─── Path: lap-and-lift (wide top trim work) ─────────────────────────────
// Long horizontal sweeps along the top, lifting at the end to flip cloth face
function pathLapLift(t) {
  const left = M + 6, right = W - M - 6, top = M + 8;
  const passes = 3;
  const phase = t * passes;
  const i = Math.min(Math.floor(phase), passes - 1);
  const local = phase - i;
  if (local < 0.65) {
    const strokeT = local / 0.65;
    return {
      x: left + strokeT * (right - left),
      y: top,
      angle: 0,
      lifted: false,
      tool: 'cloth',
      faceIndex: i, // changes color of cloth to show face rotation
    };
  } else {
    const liftT = (local - 0.65) / 0.35;
    const x = right - liftT * (right - left);
    const lift = Math.sin(liftT * Math.PI) * 22;
    const flipRot = liftT * 180;
    return {
      x,
      y: top - lift,
      angle: flipRot,
      lifted: true,
      tool: 'cloth',
      faceIndex: i,
    };
  }
}

// ─── Path: low-angle scrape ──────────────────────────────────────────────
// Forward stroke at ~25° angle, lift fully, reset to left, repeat.
// Three passes total. Each pass: forward (0..0.55), lift (0.55..0.7),
// glide back (0.7..0.95), settle (0.95..1).
function pathLowAngle(t) {
  const left = M + 60, right = W - M - 60;
  const passes = 3;
  const passSpacing = 50;
  const baseY = H / 2 - passSpacing;
  const phase = t * passes;
  const i = Math.min(Math.floor(phase), passes - 1);
  const local = phase - i;
  const y = baseY + i * passSpacing;

  // Forward stroke: blade angle stays locked at 25° (the safe angle)
  if (local < 0.55) {
    const strokeT = local / 0.55;
    const eased = strokeT < 0.1 ? strokeT * 5 : strokeT < 0.9 ? 0.5 + (strokeT - 0.5) : 0.95 + (strokeT - 0.9) * 0.5;
    const x = left + Math.min(eased, 1) * (right - left);
    return {
      x, y,
      angle: 0,
      bladeAngle: 25,
      tool: 'razor',
      lifted: false,
      scraping: true,
      pass: i,
    };
  }
  // Lift: blade rises off the glass
  if (local < 0.7) {
    const liftT = (local - 0.55) / 0.15;
    return {
      x: right,
      y: y - liftT * 28,
      angle: 0,
      bladeAngle: 25 + liftT * 30, // tilts up as it lifts
      tool: 'razor',
      lifted: true,
      scraping: false,
      pass: i,
    };
  }
  // Glide back to left, raised
  if (local < 0.95) {
    const backT = (local - 0.7) / 0.25;
    return {
      x: right - backT * (right - left),
      y: y - 28,
      angle: 0,
      bladeAngle: 55,
      tool: 'razor',
      lifted: true,
      scraping: false,
      pass: i,
    };
  }
  // Settle down for next pass
  const settleT = (local - 0.95) / 0.05;
  return {
    x: left,
    y: y - 28 + settleT * 28,
    angle: 0,
    bladeAngle: 55 - settleT * 30,
    tool: 'razor',
    lifted: settleT < 1,
    scraping: false,
    pass: i,
  };
}

// ─── Path: angle test (cycles through 4 angles to demonstrate) ───────────
// Blade stays roughly centered; cycles angle 25° → 45° → 60° → 80° → loop.
// Each angle gets a "verdict" overlay.
function pathAngleTest(t) {
  const cx = W / 2, cy = H / 2;
  const stages = 4; // 25, 45, 60, 80
  const angles = [25, 45, 60, 80];
  const phase = t * stages;
  const i = Math.min(Math.floor(phase), stages - 1);
  const local = phase - i;

  // Slight drift forward so the visual isn't completely static
  const driftX = local * 50 - 25;
  const x = cx + driftX;

  // Smoothly transition between angles
  const fromAngle = i === 0 ? angles[stages - 1] : angles[i - 1];
  const toAngle = angles[i];
  // Snap quickly at the start of each stage, then hold
  const blend = local < 0.15 ? local / 0.15 : 1;
  const eased = 0.5 - 0.5 * Math.cos(blend * Math.PI);
  const bladeAngle = fromAngle + (toAngle - fromAngle) * eased;

  return {
    x, y: cy,
    angle: 0,
    bladeAngle,
    tool: 'razor',
    lifted: false,
    scraping: bladeAngle <= 35, // visually only "scraping" at safe angles
    angleStage: i,
    angleValue: angles[i],
  };
}

// ─── Path: edge approach (entering a deposit tangentially) ───────────────
// Two cycles. Each cycle: blade approaches a deposit from outside, slides
// under the edge at low angle, lifts the deposit, retreats.
function pathEdgeApproach(t) {
  const cycles = 2;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  // Two deposits to scrape — top and bottom of pane
  const depositX = W / 2 + (i === 0 ? -60 : 60);
  const depositY = i === 0 ? H * 0.35 : H * 0.62;

  // Approach from the side, tangent to the deposit
  const approachFromX = depositX + 120;
  const approachFromY = depositY;

  if (local < 0.35) {
    // Approach
    const localEased = 0.5 - 0.5 * Math.cos((local / 0.35) * Math.PI);
    return {
      x: approachFromX - localEased * 110,
      y: depositY,
      angle: 0,
      bladeAngle: 25,
      tool: 'razor',
      lifted: false,
      scraping: false,
      atDeposit: false,
      depositX, depositY, depositActive: true,
    };
  }
  if (local < 0.75) {
    // Slide under the deposit, scraping
    const slideT = (local - 0.35) / 0.4;
    return {
      x: depositX + 10 - slideT * 80,
      y: depositY,
      angle: 0,
      bladeAngle: 22,
      tool: 'razor',
      lifted: false,
      scraping: true,
      atDeposit: true,
      depositX, depositY,
      depositActive: slideT < 0.6, // deposit shrinks as it's scraped
      depositProgress: slideT,
    };
  }
  // Lift and retreat
  const retreatT = (local - 0.75) / 0.25;
  return {
    x: depositX - 70 - retreatT * 30,
    y: depositY - retreatT * 18,
    angle: 0,
    bladeAngle: 22 + retreatT * 30,
    tool: 'razor',
    lifted: retreatT > 0.3,
    scraping: false,
    atDeposit: false,
    depositX, depositY,
    depositActive: false,
  };
}

// ─── Path: sleeve wash (the canonical pro application) ──────────────────
// Three phases: 1) load (sleeve dips into bucket and lifts), 2) edge-prime
// (perimeter pass), 3) fill (interior overlapping passes). The wet area
// expands as the sleeve passes over the glass.
function pathSleeveWash(t) {
  const left = M + 4, right = W - M - 4;
  const top = M + 4, bot = H - M - 4;

  // Phase 1 (0..0.12): load from bucket — sleeve dips below pane, rises
  if (t < 0.12) {
    const local = t / 0.12;
    // Bucket is below pane; sleeve dips into it then rises
    const dipY = H + 30;
    const startY = bot - 20;
    const restY = bot - 80; // raised position before approaching pane
    let x, y, loading;
    if (local < 0.4) {
      // Lowering into bucket
      const downT = local / 0.4;
      x = left + 60;
      y = startY + downT * (dipY - startY);
      loading = downT > 0.7;
    } else if (local < 0.7) {
      // Soaking in bucket (slight bob)
      const soakT = (local - 0.4) / 0.3;
      x = left + 60 + Math.sin(soakT * Math.PI * 3) * 4;
      y = dipY;
      loading = true;
    } else {
      // Rising loaded
      const upT = (local - 0.7) / 0.3;
      x = left + 60;
      y = dipY - upT * (dipY - restY);
      loading = false;
    }
    return {
      x, y,
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      depositing: false,
      isLoading: loading,
      phase: 'load',
    };
  }

  // Phase 2 (0.12..0.32): edge prime — quick perimeter pass on top edge
  if (t < 0.32) {
    const local = (t - 0.12) / 0.20;
    // Diagonal-down then horizontal across top
    if (local < 0.15) {
      // Move from rest position to top-left corner
      const moveT = local / 0.15;
      const eased = 0.5 - 0.5 * Math.cos(moveT * Math.PI);
      return {
        x: left + 60 - eased * 60 + (eased * (left - (left + 60))),
        y: bot - 80 + eased * (top + 12 - (bot - 80)),
        angle: 0,
        tool: 'sleeve',
        lifted: true,
        depositing: false,
        phase: 'edge',
      };
    } else {
      // Sweep across top
      const sweepT = (local - 0.15) / 0.85;
      return {
        x: left + sweepT * (right - left),
        y: top + 12,
        angle: 0,
        tool: 'sleeve',
        lifted: false,
        depositing: true,
        phase: 'edge',
      };
    }
  }

  // Phase 3 (0.32..1.0): fill interior with overlapping vertical-ish passes
  // Top-to-bottom serpentine. The sleeve goes down a column, slides over,
  // goes up the next, slides over, etc. Three columns total.
  const local = (t - 0.32) / 0.68;
  const columns = 3;
  const colWidth = (right - left) / columns;
  const phase = local * columns;
  const i = Math.min(Math.floor(phase), columns - 1);
  const colLocal = phase - i;

  const colCenterX = left + (i + 0.5) * colWidth;
  const goingDown = i % 2 === 0;

  // Each column: down (or up) the height of the pane
  const yStart = goingDown ? top + 30 : bot - 12;
  const yEnd = goingDown ? bot - 12 : top + 30;
  const eased = 0.5 - 0.5 * Math.cos(colLocal * Math.PI);
  const y = yStart + eased * (yEnd - yStart);

  // Slight wobble in x to suggest the sleeve drag
  const wobble = Math.sin(colLocal * Math.PI * 4) * 1.5;

  return {
    x: colCenterX + wobble,
    y,
    angle: 0,
    tool: 'sleeve',
    lifted: false,
    depositing: true,
    columnIndex: i,
    phase: 'fill',
  };
}

// ─── Path: sea sponge dab (older technique, characteristic dabbing motion) ──
// The sponge dabs in a grid pattern, partially overlapping. Each dab is a
// brief downward press + lift. Slower, more deliberate than the sleeve.
function pathSpongeDab(t) {
  const left = M + 30, right = W - M - 30;
  const top = M + 30, bot = H - M - 30;
  const cols = 5;
  const rows = 4;
  const total = cols * rows;
  const colSpacing = (right - left) / (cols - 1);
  const rowSpacing = (bot - top) / (rows - 1);

  const phase = t * total;
  const i = Math.min(Math.floor(phase), total - 1);
  const local = phase - i;

  // Snake order: left-to-right on even rows, right-to-left on odd
  const row = Math.floor(i / cols);
  const colInRow = i % cols;
  const col = row % 2 === 0 ? colInRow : (cols - 1 - colInRow);

  // Target dab position
  const targetX = left + col * colSpacing;
  const targetY = top + row * rowSpacing;

  // Previous dab position (for movement between)
  let prevTargetX, prevTargetY;
  if (i === 0) {
    prevTargetX = left - 40;
    prevTargetY = top - 20;
  } else {
    const prevI = i - 1;
    const prevRow = Math.floor(prevI / cols);
    const prevColInRow = prevI % cols;
    const prevCol = prevRow % 2 === 0 ? prevColInRow : (cols - 1 - prevColInRow);
    prevTargetX = left + prevCol * colSpacing;
    prevTargetY = top + prevRow * rowSpacing;
  }

  // Each dab cycle: move (0..0.5) → press (0.5..0.7) → lift (0.7..1)
  if (local < 0.5) {
    const moveT = local / 0.5;
    const eased = 0.5 - 0.5 * Math.cos(moveT * Math.PI);
    return {
      x: prevTargetX + (targetX - prevTargetX) * eased,
      y: prevTargetY - 14 + (targetY - 14 - (prevTargetY - 14)) * eased,
      angle: 0,
      tool: 'sponge',
      lifted: true,
      depositing: false,
      phase: 'move',
      dabIndex: i,
    };
  } else if (local < 0.7) {
    const pressT = (local - 0.5) / 0.2;
    // Press down: blade descends to surface, scale grows slightly
    const eased = Math.sin(pressT * Math.PI / 2);
    return {
      x: targetX,
      y: targetY - 14 + eased * 14,
      angle: 0,
      tool: 'sponge',
      lifted: pressT < 0.5,
      depositing: pressT > 0.4,
      pressing: pressT > 0.5,
      scale: 1 + eased * 0.08,
      phase: 'press',
      dabIndex: i,
    };
  } else {
    const liftT = (local - 0.7) / 0.3;
    const eased = 0.5 - 0.5 * Math.cos(liftT * Math.PI);
    return {
      x: targetX,
      y: targetY - eased * 14,
      angle: 0,
      tool: 'sponge',
      lifted: eased > 0.3,
      depositing: false,
      phase: 'lift',
      dabIndex: i,
    };
  }
}

// ─── Path: spray-and-spread (homeowner technique) ───────────────────────
// Two phases: 1) spray bottle visits 5 spots, mists each (visible droplet
// burst), 2) microfiber spreads the mist in horizontal passes.
function pathSprayAndSpread(t) {
  const left = M + 20, right = W - M - 20;
  const top = M + 30, bot = H - M - 30;

  // Phase 1 (0..0.4): spray 5 spots
  if (t < 0.4) {
    const local = t / 0.4;
    const sprayCount = 5;
    const phase = local * sprayCount;
    const i = Math.min(Math.floor(phase), sprayCount - 1);
    const sprayLocal = phase - i;

    // 5 spray targets distributed across the pane
    const targets = [
      { x: left + 30, y: top + 30 },
      { x: right - 30, y: top + 30 },
      { x: (left + right) / 2, y: (top + bot) / 2 },
      { x: left + 30, y: bot - 30 },
      { x: right - 30, y: bot - 30 },
    ];

    const target = targets[i];
    const prev = i === 0 ? { x: left - 50, y: top - 20 } : targets[i - 1];

    if (sprayLocal < 0.55) {
      const moveT = sprayLocal / 0.55;
      const eased = 0.5 - 0.5 * Math.cos(moveT * Math.PI);
      return {
        x: prev.x + (target.x - prev.x) * eased,
        y: prev.y + (target.y - prev.y) * eased,
        angle: 0,
        tool: 'spray',
        lifted: true,
        depositing: false,
        spraying: false,
        phase: 'spray',
        sprayIndex: i,
      };
    } else {
      // Spray burst
      return {
        x: target.x,
        y: target.y,
        angle: 0,
        tool: 'spray',
        lifted: true,
        depositing: true,
        spraying: true,
        phase: 'spray',
        sprayIndex: i,
        sprayBurstT: (sprayLocal - 0.55) / 0.45,
      };
    }
  }

  // Phase 2 (0.4..1.0): spread with cloth in horizontal passes
  const local = (t - 0.4) / 0.6;
  const passes = 4;
  const passSpacing = (bot - top) / (passes - 1);
  const phase = local * passes;
  const i = Math.min(Math.floor(phase), passes - 1);
  const passLocal = phase - i;
  const y = top + i * passSpacing;

  if (passLocal < 0.7) {
    const strokeT = passLocal / 0.7;
    return {
      x: left + strokeT * (right - left),
      y,
      angle: 0,
      tool: 'cloth',
      lifted: false,
      depositing: false, // spreading, not depositing
      faceIndex: i,
      phase: 'spread',
    };
  } else {
    const liftT = (passLocal - 0.7) / 0.3;
    const x = right - liftT * (right - left);
    const lift = Math.sin(liftT * Math.PI) * 14;
    return {
      x, y: y - lift,
      angle: 0,
      tool: 'cloth',
      lifted: true,
      depositing: false,
      faceIndex: i,
      phase: 'spread',
    };
  }
}

// ─── Path: cross-body wipe (squeegee → off-hand cloth → back) ────────────
// Three cycles. Each cycle: squeegee finishes a downstroke on the pane,
// crosses to the cloth (positioned bottom-left), wipes both sides, returns
// to the top-right to start the next stroke.
function pathCrossBodyWipe(t) {
  const left = M + 30, right = W - M - 30;
  const top = M + 20, bot = H - M - 30;
  const clothX = M - 10;       // cloth target is just outside the pane, left side
  const clothY = H - M + 8;    // and just below it (held at hip)
  const cycles = 3;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  // Each pane stroke offsets a bit so we see distinct passes
  const strokeOffsetX = i * 60;
  const strokeStartX = left + strokeOffsetX;
  const strokeEndX = strokeStartX;
  const strokeStartY = top;
  const strokeEndY = bot - 30;

  // Phases inside one cycle:
  //   0.00–0.30  downstroke on pane
  //   0.30–0.45  travel from pane to cloth
  //   0.45–0.55  wipe pass 1 (down)
  //   0.55–0.62  flip rotation
  //   0.62–0.72  wipe pass 2 (up)
  //   0.72–0.90  travel back to start of next stroke
  //   0.90–1.00  reset / hover

  if (local < 0.30) {
    const sT = local / 0.30;
    const eased = 0.5 - 0.5 * Math.cos(sT * Math.PI);
    return {
      x: strokeStartX + eased * (strokeEndX - strokeStartX),
      y: strokeStartY + eased * (strokeEndY - strokeStartY),
      angle: 0,
      tool: 'squeegee',
      lifted: false,
      onPane: true,
      atCloth: false,
      phase: 'stroke',
      pass: i,
    };
  }
  if (local < 0.45) {
    const tT = (local - 0.30) / 0.15;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    const x = strokeEndX + (clothX - strokeEndX) * eased;
    const y = strokeEndY + (clothY - strokeEndY) * eased - Math.sin(tT * Math.PI) * 16;
    return {
      x, y,
      angle: -8 * eased,  // tilt while crossing
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: false,
      phase: 'travel',
      pass: i,
    };
  }
  if (local < 0.55) {
    // Wipe pass 1 — drag down across cloth
    const wT = (local - 0.45) / 0.10;
    return {
      x: clothX,
      y: clothY - 14 + wT * 28,
      angle: -8,
      tool: 'squeegee',
      lifted: false,
      onPane: false,
      atCloth: true,
      wiping: true,
      wipeDirection: 1,
      phase: 'wipe',
      pass: i,
    };
  }
  if (local < 0.62) {
    // Flip — quick blade rotation
    const fT = (local - 0.55) / 0.07;
    return {
      x: clothX,
      y: clothY + 14,
      angle: -8 + fT * 196,    // rotates ~180°
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: true,
      wiping: false,
      flipping: true,
      phase: 'flip',
      pass: i,
    };
  }
  if (local < 0.72) {
    // Wipe pass 2 — drag up
    const wT = (local - 0.62) / 0.10;
    return {
      x: clothX,
      y: clothY + 14 - wT * 28,
      angle: 188,
      tool: 'squeegee',
      lifted: false,
      onPane: false,
      atCloth: true,
      wiping: true,
      wipeDirection: -1,
      phase: 'wipe',
      pass: i,
    };
  }
  if (local < 0.90) {
    // Travel back to next stroke start (or just hover if last)
    const tT = (local - 0.72) / 0.18;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    const nextStrokeX = left + Math.min(i + 1, cycles - 1) * 60;
    const x = clothX + (nextStrokeX - clothX) * eased;
    const y = clothY - 14 + (top - (clothY - 14)) * eased - Math.sin(tT * Math.PI) * 12;
    // Smoothly rotate back to 0
    const angle = 188 - eased * 188;
    return {
      x, y,
      angle: angle % 360,
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: false,
      phase: 'return',
      pass: i,
    };
  }
  // Reset hover at next stroke start
  return {
    x: left + Math.min(i + 1, cycles - 1) * 60,
    y: top,
    angle: 0,
    tool: 'squeegee',
    lifted: true,
    onPane: false,
    atCloth: false,
    phase: 'ready',
    pass: i,
  };
}

// ─── Path: thigh wipe (fast production move, cloth tucked at hip) ────────
// Like cross-body but faster, smaller motion, only one direction per wipe.
// Five cycles in same total duration → frenetic pace.
function pathThighWipe(t) {
  const left = M + 30, right = W - M - 30;
  const top = M + 20, bot = H - M - 30;
  // Cloth target: bottom-center, simulating tucked at the cleaner's hip
  const clothX = (left + right) / 2;
  const clothY = H - M + 8;
  const cycles = 5;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  const strokeX = left + i * ((right - left) / (cycles - 1));
  const strokeStartY = top;
  const strokeEndY = bot - 40;

  if (local < 0.40) {
    // Quick downstroke
    const sT = local / 0.40;
    const eased = 0.5 - 0.5 * Math.cos(sT * Math.PI);
    return {
      x: strokeX,
      y: strokeStartY + eased * (strokeEndY - strokeStartY),
      angle: 0,
      tool: 'squeegee',
      lifted: false,
      onPane: true,
      atCloth: false,
      phase: 'stroke',
      pass: i,
    };
  }
  if (local < 0.55) {
    // Travel to thigh
    const tT = (local - 0.40) / 0.15;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    return {
      x: strokeX + (clothX - strokeX) * eased,
      y: strokeEndY + (clothY - strokeEndY) * eased - Math.sin(tT * Math.PI) * 14,
      angle: -6 * eased,
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: false,
      phase: 'travel',
      pass: i,
    };
  }
  if (local < 0.70) {
    // Single quick wipe — short downward swipe
    const wT = (local - 0.55) / 0.15;
    return {
      x: clothX - 8 + wT * 16,  // slight horizontal slide
      y: clothY - 8 + wT * 16,
      angle: -10,
      tool: 'squeegee',
      lifted: false,
      onPane: false,
      atCloth: true,
      wiping: true,
      wipeDirection: 1,
      phase: 'wipe',
      pass: i,
    };
  }
  if (local < 0.95) {
    // Travel back
    const tT = (local - 0.70) / 0.25;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    const nextStrokeX = left + Math.min(i + 1, cycles - 1) * ((right - left) / (cycles - 1));
    return {
      x: (clothX + 8) + (nextStrokeX - (clothX + 8)) * eased,
      y: (clothY + 8) + (top - (clothY + 8)) * eased - Math.sin(tT * Math.PI) * 14,
      angle: -10 + eased * 10,
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: false,
      phase: 'return',
      pass: i,
    };
  }
  return {
    x: left + Math.min(i + 1, cycles - 1) * ((right - left) / (cycles - 1)),
    y: top,
    angle: 0,
    tool: 'squeegee',
    lifted: true,
    onPane: false,
    atCloth: false,
    phase: 'ready',
    pass: i,
  };
}

// ─── Path: bucket-edge wipe (compromise, blade drags along bucket rim) ───
// Cloth target replaced by a bucket rim line at the bottom of the scene.
// The blade drags along the rim — the technique that pros use only when
// they have no cloth handy. Two cycles, slow.
function pathBucketEdgeWipe(t) {
  const left = M + 40, right = W - M - 40;
  const top = M + 20, bot = H - M - 30;
  // Bucket rim runs horizontally just below the pane
  const rimY = H - M + 14;
  const rimStartX = (left + right) / 2 - 50;
  const rimEndX = (left + right) / 2 + 50;
  const cycles = 2;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  const strokeX = left + i * ((right - left) / (cycles - 1) - 40);
  const strokeStartY = top;
  const strokeEndY = bot - 50;

  if (local < 0.30) {
    const sT = local / 0.30;
    const eased = 0.5 - 0.5 * Math.cos(sT * Math.PI);
    return {
      x: strokeX,
      y: strokeStartY + eased * (strokeEndY - strokeStartY),
      angle: 0,
      tool: 'squeegee',
      lifted: false,
      onPane: true,
      atCloth: false,
      phase: 'stroke',
      pass: i,
    };
  }
  if (local < 0.45) {
    const tT = (local - 0.30) / 0.15;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    return {
      x: strokeX + (rimStartX - strokeX) * eased,
      y: strokeEndY + (rimY - strokeEndY) * eased - Math.sin(tT * Math.PI) * 14,
      angle: 0,
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: false,
      phase: 'travel',
      pass: i,
    };
  }
  if (local < 0.70) {
    // Drag along bucket rim — long horizontal sweep
    const dragT = (local - 0.45) / 0.25;
    return {
      x: rimStartX + dragT * (rimEndX - rimStartX),
      y: rimY,
      angle: 0,
      tool: 'squeegee',
      lifted: false,
      onPane: false,
      atCloth: true,
      wiping: true,
      onRim: true,
      phase: 'wipe',
      pass: i,
    };
  }
  if (local < 0.95) {
    const tT = (local - 0.70) / 0.25;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    const nextStrokeX = left + Math.min(i + 1, cycles - 1) * ((right - left) / (cycles - 1) - 40);
    return {
      x: rimEndX + (nextStrokeX - rimEndX) * eased,
      y: rimY + (top - rimY) * eased - Math.sin(tT * Math.PI) * 14,
      angle: 0,
      tool: 'squeegee',
      lifted: true,
      onPane: false,
      atCloth: false,
      phase: 'return',
      pass: i,
    };
  }
  return {
    x: left + Math.min(i + 1, cycles - 1) * ((right - left) / (cycles - 1) - 40),
    y: top,
    angle: 0,
    tool: 'squeegee',
    lifted: true,
    onPane: false,
    atCloth: false,
    phase: 'ready',
    pass: i,
  };
}

// ─── Path: two-bucket system (sleeve cycles between wash + rinse) ───────
// Visual setup: WASH bucket on the left, RINSE bucket on the right,
// pane in the middle. Sleeve travels: wash → squeeze → pane work →
// rinse → squeeze → back to wash. Three full cycles.
function pathTwoBucket(t) {
  // Anchor positions
  const washX = M + 70;
  const rinseX = W - M - 70;
  const bucketY = H - M + 4; // top of bucket rim
  const dipY = H - M + 38;   // below rim, inside bucket
  const restY = H / 2 + 30;  // mid-pane height for transit
  const paneCenterX = W / 2;
  const paneWorkY = H / 2;

  const cycles = 3;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  // Per-cycle phases:
  //   0.00–0.10  dip in WASH
  //   0.10–0.16  rise from wash
  //   0.16–0.30  travel to pane
  //   0.30–0.50  apply on pane (short downstroke)
  //   0.50–0.65  travel to RINSE
  //   0.65–0.75  dip in rinse
  //   0.75–0.85  rise from rinse, squeeze
  //   0.85–1.00  travel back to wash position

  if (local < 0.10) {
    // Dip into wash
    const dT = local / 0.10;
    return {
      x: washX,
      y: restY + dT * (dipY - restY),
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      atBucket: 'wash',
      isLoading: dT > 0.5,
      depositing: false,
      phase: 'wash-dip',
      pass: i,
    };
  }
  if (local < 0.16) {
    const rT = (local - 0.10) / 0.06;
    return {
      x: washX,
      y: dipY + rT * (bucketY - dipY),
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      atBucket: 'wash',
      isLoading: rT < 0.7,
      depositing: false,
      phase: 'wash-rise',
      pass: i,
    };
  }
  if (local < 0.30) {
    // Travel to pane
    const tT = (local - 0.16) / 0.14;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    return {
      x: washX + (paneCenterX - washX) * eased,
      y: bucketY + (paneWorkY - bucketY) * eased - Math.sin(tT * Math.PI) * 12,
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      isLoading: false,
      depositing: false,
      phase: 'travel-to-pane',
      pass: i,
    };
  }
  if (local < 0.50) {
    // Apply on pane — short controlled stroke
    const sT = (local - 0.30) / 0.20;
    return {
      x: paneCenterX,
      y: paneWorkY - 30 + sT * 60,
      angle: 0,
      tool: 'sleeve',
      lifted: false,
      onPane: true,
      depositing: true,
      phase: 'apply',
      pass: i,
    };
  }
  if (local < 0.65) {
    // Travel to rinse
    const tT = (local - 0.50) / 0.15;
    const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
    return {
      x: paneCenterX + (rinseX - paneCenterX) * eased,
      y: (paneWorkY + 30) + (bucketY - (paneWorkY + 30)) * eased - Math.sin(tT * Math.PI) * 12,
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      depositing: false,
      phase: 'travel-to-rinse',
      pass: i,
    };
  }
  if (local < 0.75) {
    const dT = (local - 0.65) / 0.10;
    return {
      x: rinseX,
      y: bucketY + dT * (dipY - bucketY),
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      atBucket: 'rinse',
      isLoading: dT > 0.5,
      depositing: false,
      phase: 'rinse-dip',
      pass: i,
    };
  }
  if (local < 0.85) {
    const rT = (local - 0.75) / 0.10;
    return {
      x: rinseX,
      y: dipY + rT * (bucketY - dipY),
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      atBucket: 'rinse',
      isLoading: rT < 0.5,
      depositing: false,
      phase: 'rinse-rise',
      pass: i,
    };
  }
  // Travel back to wash
  const tT = (local - 0.85) / 0.15;
  const eased = 0.5 - 0.5 * Math.cos(tT * Math.PI);
  return {
    x: rinseX + (washX - rinseX) * eased,
    y: bucketY + (restY - bucketY) * eased - Math.sin(tT * Math.PI) * 14,
    angle: 0,
    tool: 'sleeve',
    lifted: true,
    onPane: false,
    depositing: false,
    phase: 'return-to-wash',
    pass: i,
  };
}

// ─── Path: sleeve loading (deliberate four-step) ────────────────────────
// Slow, instructional. Submerge, twist, lift, rim-squeeze. One cycle, long.
// Single bucket centered below the pane.
function pathSleeveLoad(t) {
  const bucketX = W / 2;
  const restY = H / 2 + 40;
  const submergeY = H - M + 40;
  const rimY = H - M + 4;

  // Phases:
  //   0.00–0.20  approach + submerge
  //   0.20–0.40  twist (rotate at submerged depth)
  //   0.40–0.55  lift to rim
  //   0.55–0.85  squeeze along rim (slow drag)
  //   0.85–1.00  raise to ready position

  if (t < 0.20) {
    const local = t / 0.20;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: bucketX,
      y: restY + (submergeY - restY) * eased,
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      isLoading: local > 0.6,
      depositing: false,
      phase: 'submerge',
    };
  }
  if (t < 0.40) {
    const local = (t - 0.20) / 0.20;
    // Twist — visible rotation while submerged
    return {
      x: bucketX,
      y: submergeY,
      angle: Math.sin(local * Math.PI * 4) * 14,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      isLoading: true,
      depositing: false,
      phase: 'twist',
    };
  }
  if (t < 0.55) {
    const local = (t - 0.40) / 0.15;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: bucketX,
      y: submergeY + (rimY - submergeY) * eased,
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      isLoading: true,
      depositing: false,
      phase: 'lift',
    };
  }
  if (t < 0.85) {
    const local = (t - 0.55) / 0.30;
    // Squeeze along rim — slight horizontal motion as if dragging
    return {
      x: bucketX - 12 + local * 24,
      y: rimY,
      angle: 0,
      tool: 'sleeve',
      lifted: true,
      onPane: false,
      isLoading: local < 0.7,
      squeezing: true,
      depositing: false,
      phase: 'squeeze',
    };
  }
  const local = (t - 0.85) / 0.15;
  const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
  return {
    x: bucketX,
    y: rimY + (restY - rimY) * eased,
    angle: 0,
    tool: 'sleeve',
    lifted: true,
    onPane: false,
    isLoading: false,
    depositing: false,
    phase: 'ready',
  };
}

// ─── Path: tool layout (stop-motion appearance of all worksite items) ───
// Items appear one by one in their canonical positions on the floor/pane
// surface. Currently animates a "ghost cursor" tool dropping each item
// into place. Marker for item-revealed-by-now is exposed via t.
function pathToolLayout(t) {
  // The "tool" here is just a moving marker — we use a small cursor
  // pseudo-tool. The actual scene draws the items based on t.
  const items = 6;
  const phase = t * items;
  const i = Math.min(Math.floor(phase), items - 1);
  const local = phase - i;

  // Item drop positions (correspond to scene markers)
  const positions = [
    { x: M + 80,  y: H - M + 4,  label: 'WASH BUCKET' },
    { x: W - M - 80, y: H - M + 4, label: 'RINSE BUCKET' },
    { x: M + 30,  y: H - M - 40, label: 'SQUEEGEE' },
    { x: M + 30,  y: H - M - 20, label: 'SCRAPER' },
    { x: W - M - 30, y: H - M - 40, label: 'CLOTHS' },
    { x: W / 2,    y: H - M - 16, label: 'APRON' },
  ];

  const target = positions[i];
  const fromY = -50; // items drop from above

  if (local < 0.6) {
    // Drop into position
    const eased = local / 0.6;
    const drop = 0.5 - 0.5 * Math.cos(eased * Math.PI);
    return {
      x: target.x,
      y: fromY + (target.y - fromY) * drop,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      onPane: false,
      depositing: false,
      itemIndex: i,
      itemLabel: target.label,
      itemDropping: true,
      phase: 'drop',
    };
  }
  return {
    x: target.x,
    y: target.y - 18,
    angle: 0,
    tool: 'cursor',
    lifted: true,
    onPane: false,
    depositing: false,
    itemIndex: i,
    itemLabel: target.label,
    itemDropping: false,
    phase: 'place',
  };
}

// ─── Path: sash tilt-in (disengage latches, pivot sash inward) ──────────
// The sash itself is the actor here. The "tool" is a hand cursor that
// activates the latches at the top, then guides the sash as it pivots.
// Phases:
//   0.00–0.20  approach top-left latch
//   0.20–0.30  release latch (visual flash)
//   0.30–0.50  approach top-right latch
//   0.50–0.60  release latch
//   0.60–1.00  guide sash inward (sash pivots from vertical to ~45°)
function pathSashTiltIn(t) {
  const leftLatchX = M + 50;
  const rightLatchX = W - M - 50;
  const latchY = M + 14;
  const guideX = W / 2;
  const guideY = H / 2;

  if (t < 0.20) {
    const local = t / 0.20;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: M - 40 + eased * (leftLatchX - (M - 40)),
      y: H / 2 + eased * (latchY - H / 2),
      angle: 0,
      tool: 'cursor',
      lifted: true,
      onPane: false,
      depositing: false,
      atLatch: false,
      latchSide: null,
      sashAngle: 0,
      phase: 'approach-left',
    };
  }
  if (t < 0.30) {
    return {
      x: leftLatchX,
      y: latchY,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: true,
      latchSide: 'left',
      sashAngle: 0,
      phase: 'release-left',
    };
  }
  if (t < 0.50) {
    const local = (t - 0.30) / 0.20;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: leftLatchX + eased * (rightLatchX - leftLatchX),
      y: latchY,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: false,
      latchSide: null,
      latchLeftReleased: true,
      sashAngle: 0,
      phase: 'approach-right',
    };
  }
  if (t < 0.60) {
    return {
      x: rightLatchX,
      y: latchY,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: true,
      latchSide: 'right',
      latchLeftReleased: true,
      sashAngle: 0,
      phase: 'release-right',
    };
  }
  // Tilt the sash inward — cursor descends with it, guiding
  const local = (t - 0.60) / 0.40;
  const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
  const sashAngle = eased * 45; // 0° to 45°
  return {
    x: guideX,
    y: latchY + eased * (guideY - latchY),
    angle: 0,
    tool: 'cursor',
    lifted: true,
    atLatch: false,
    latchLeftReleased: true,
    latchRightReleased: true,
    sashAngle,
    phase: 'guide-tilt',
  };
}

// ─── Path: reach-across (cleaning outer pane through tilted sash) ───────
// Sash is locked at 45° tilt-in (pre-set). Squeegee reaches the now-exposed
// outer face and performs a controlled S-stroke. Body-position indicators
// flash if the reach extends beyond safe envelope.
function pathReachAcross(t) {
  // Outer pane is "behind" the sash, accessed at an angle. We render the
  // squeegee on a virtual outer pane that sits behind/beneath the tilted sash.
  // For animation, treat it as a normal S-stroke but mark the lean state.
  const left = M + 70, right = W - M - 70;
  const top = M + 60, bot = H - M - 80;

  if (t < 0.4) {
    const local = t / 0.4;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: left + eased * (right - left),
      y: top + local * ((bot - top) * 0.4),
      angle: -20 + 40 * eased,
      tool: 'squeegee',
      lifted: false,
      onPane: true,
      isReaching: true,
      reachExtent: Math.abs(eased - 0.5) * 2, // 0 at center, 1 at extremes
      sashAngle: 45,
      phase: 'reach-out',
    };
  }
  const local = (t - 0.4) / 0.6;
  const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
  return {
    x: right - eased * (right - left),
    y: top + (bot - top) * 0.4 + local * ((bot - top) * 0.6),
    angle: 20 - 40 * eased,
    tool: 'squeegee',
    lifted: false,
    onPane: true,
    isReaching: true,
    reachExtent: Math.abs(eased - 0.5) * 2,
    sashAngle: 45,
    phase: 'reach-back',
  };
}

// ─── Path: re-seat (pivot the wet sash back, re-engage latches) ─────────
// Reverse of the tilt-in: lift the sash from 45° back to vertical, then
// engage each latch. The trick is the sash is now wet and slightly heavier.
function pathReSeat(t) {
  const leftLatchX = M + 50;
  const rightLatchX = W - M - 50;
  const latchY = M + 14;
  const guideX = W / 2;
  const guideY = H / 2;

  if (t < 0.40) {
    // Lift sash from 45° back to vertical
    const local = t / 0.40;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    const sashAngle = 45 - eased * 45;
    return {
      x: guideX,
      y: guideY - eased * (guideY - latchY),
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: false,
      sashAngle,
      isReseating: true,
      phase: 'lift',
    };
  }
  if (t < 0.55) {
    // Approach left latch
    const local = (t - 0.40) / 0.15;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: guideX + (leftLatchX - guideX) * eased,
      y: latchY,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: false,
      sashAngle: 0,
      phase: 'approach-left-lock',
    };
  }
  if (t < 0.65) {
    return {
      x: leftLatchX,
      y: latchY,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: true,
      latchSide: 'left',
      isLocking: true,
      sashAngle: 0,
      latchLeftLocked: true,
      phase: 'lock-left',
    };
  }
  if (t < 0.85) {
    const local = (t - 0.65) / 0.20;
    const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
    return {
      x: leftLatchX + eased * (rightLatchX - leftLatchX),
      y: latchY,
      angle: 0,
      tool: 'cursor',
      lifted: true,
      atLatch: false,
      latchLeftLocked: true,
      sashAngle: 0,
      phase: 'approach-right-lock',
    };
  }
  return {
    x: rightLatchX,
    y: latchY,
    angle: 0,
    tool: 'cursor',
    lifted: true,
    atLatch: true,
    latchSide: 'right',
    isLocking: true,
    latchLeftLocked: true,
    latchRightLocked: true,
    sashAngle: 0,
    phase: 'lock-right',
  };
}

// ─── Path: low-angle pole (controlling a long extension at low pole angle) ──
// The pane is drawn smaller in the upper portion of the canvas. The brush
// head moves on the pane while a long pole connects down to a "ground"
// pivot point at the bottom of the canvas. The teaching is "small base
// motion = big head motion."
//
// Three demonstration sweeps showing the leverage problem:
//   1. Small base movement → moderate head movement (controlled)
//   2. Same base movement, longer pole → larger head movement (drift)
//   3. Compensating: deliberate small adjustments produce smooth path
function pathLowAnglePole(t) {
  // Pane area is upper third only — implying second-story window
  const paneTop = M + 4;
  const paneBot = M + 130;
  const paneLeft = M + 60;
  const paneRight = W - M - 60;

  // Three demonstration cycles
  const cycles = 3;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  // Each cycle: a sweep from left to right across the pane at slightly
  // different "wobble" levels to teach the leverage point
  const eased = 0.5 - 0.5 * Math.cos(local * Math.PI);
  const baseX = paneLeft + eased * (paneRight - paneLeft);

  // Wobble grows with cycle index until the third (compensated)
  const wobble = i === 0 ? 2
    : i === 1 ? 8
    : Math.sin(local * Math.PI * 4) * 1.2; // smooth corrected motion
  const y = paneTop + 30 + Math.sin(local * Math.PI * 2) * wobble;

  return {
    x: baseX,
    y,
    angle: 0,
    tool: 'poleHead',
    lifted: false,
    onPane: true,
    depositing: true,
    cycleIndex: i,
    wobbleLevel: i === 0 ? 'controlled' : i === 1 ? 'drifting' : 'compensated',
    phase: i === 0 ? 'short-pole' : i === 1 ? 'long-pole' : 'corrected',
    pass: i,
  };
}

// ─── Path: two-story sweep (full pane wash with no squeegee, just rinse) ──
// Brush moves through three phases per pane: scrub (back-and-forth),
// rinse (slow drag with water flowing), and the gravity dry (brush off,
// water sheets down). Two full panes shown.
function pathTwoStorySweep(t) {
  const paneTop = M + 4;
  const paneBot = M + 140;
  const paneLeft = M + 50;
  const paneRight = W - M - 50;

  const cycles = 2;
  const phase = t * cycles;
  const i = Math.min(Math.floor(phase), cycles - 1);
  const local = phase - i;

  // Per cycle:
  //   0.00–0.40  scrub: rapid back-and-forth across pane width
  //   0.40–0.70  rinse: slow vertical drags, water flowing
  //   0.70–1.00  gravity dry: brush moves off, sheeting visualized

  if (local < 0.40) {
    // Scrub phase — fast horizontal oscillation
    const scrubT = local / 0.40;
    const scrubFreq = 4; // 4 back-and-forths
    const scrubPhase = scrubT * scrubFreq;
    const sub = scrubPhase - Math.floor(scrubPhase);
    const goingRight = Math.floor(scrubPhase) % 2 === 0;
    const x = goingRight
      ? paneLeft + sub * (paneRight - paneLeft)
      : paneRight - sub * (paneRight - paneLeft);
    // Walks down the pane gradually
    const y = paneTop + 30 + scrubT * (paneBot - paneTop - 60);

    return {
      x, y,
      angle: 0,
      tool: 'poleHead',
      lifted: false,
      onPane: true,
      depositing: true,
      scrubbing: true,
      rinsing: false,
      phase: 'scrub',
      pass: i,
    };
  }
  if (local < 0.70) {
    // Rinse phase — slow vertical drag with water flowing
    const rinseT = (local - 0.40) / 0.30;
    // Three vertical passes, top to bottom
    const passes = 3;
    const passPhase = rinseT * passes;
    const passI = Math.min(Math.floor(passPhase), passes - 1);
    const passLocal = passPhase - passI;
    const x = paneLeft + (passI + 0.5) * ((paneRight - paneLeft) / passes);
    const y = paneTop + 20 + passLocal * (paneBot - paneTop - 40);

    return {
      x, y,
      angle: 0,
      tool: 'poleHead',
      lifted: false,
      onPane: true,
      depositing: true,
      scrubbing: false,
      rinsing: true,
      phase: 'rinse',
      pass: i,
    };
  }
  // Gravity dry phase — brush moves off, water sheets down
  const dryT = (local - 0.70) / 0.30;
  const eased = 0.5 - 0.5 * Math.cos(dryT * Math.PI);
  return {
    x: paneRight + eased * 60,
    y: paneTop + 40,
    angle: 0,
    tool: 'poleHead',
    lifted: dryT > 0.3,
    onPane: false,
    depositing: false,
    scrubbing: false,
    rinsing: false,
    sheeting: true,
    sheetingT: dryT,
    phase: 'gravity-dry',
    pass: i,
  };
}

// ─── Path: brush-and-rinse (dual-function head, alternating scrub/rinse) ──
// The brush head has bristles AND water jets. This animation alternates
// between bristle-active (scrub) and jet-active (rinse) phases in a
// deliberate teaching rhythm. Single pane, slower pace.
function pathBrushAndRinse(t) {
  const paneTop = M + 8;
  const paneBot = M + 140;
  const paneLeft = M + 80;
  const paneRight = W - M - 80;
  const cx = (paneLeft + paneRight) / 2;
  const cy = (paneTop + paneBot) / 2;

  // Five alternating segments: scrub, rinse, scrub, rinse, finish
  const segments = 5;
  const phase = t * segments;
  const i = Math.min(Math.floor(phase), segments - 1);
  const local = phase - i;

  // Different motion per segment type
  if (i === 0 || i === 2) {
    // Scrub — small circular/figure-8 motion
    const angle = local * Math.PI * 4;
    const r = 30;
    const offsetX = i === 0 ? -50 : 50;
    return {
      x: cx + offsetX + Math.cos(angle) * r,
      y: cy + Math.sin(angle * 2) * (r * 0.6),
      angle: 0,
      tool: 'poleHead',
      lifted: false,
      onPane: true,
      depositing: true,
      scrubbing: true,
      rinsing: false,
      bristlesActive: true,
      jetsActive: false,
      phase: 'scrub',
      pass: Math.floor(i / 2),
    };
  }
  if (i === 1 || i === 3) {
    // Rinse — straight vertical drag
    const startY = paneTop + 20;
    const endY = paneBot - 20;
    const x = i === 1 ? cx - 50 : cx + 50;
    return {
      x,
      y: startY + local * (endY - startY),
      angle: 0,
      tool: 'poleHead',
      lifted: false,
      onPane: true,
      depositing: true,
      scrubbing: false,
      rinsing: true,
      bristlesActive: false,
      jetsActive: true,
      phase: 'rinse',
      pass: Math.floor(i / 2),
    };
  }
  // Finishing rinse — pulls down center
  const finishY = paneTop + 20 + local * (paneBot - paneTop - 40);
  return {
    x: cx,
    y: finishY,
    angle: 0,
    tool: 'poleHead',
    lifted: false,
    onPane: true,
    depositing: true,
    scrubbing: false,
    rinsing: true,
    bristlesActive: false,
    jetsActive: true,
    phase: 'finish',
    pass: 2,
  };
}

// ═════════════════════════════════════════════════════════════════════════
// CHAPTERS — single source of truth for all techniques
// ═════════════════════════════════════════════════════════════════════════
const CHAPTERS = [
  {
    id: 'squeegee',
    number: '01',
    name: 'The Squeegee Strokes',
    italic: 'three patterns, one tool',
    summary:
      'The defining motion of the trade. Three canonical strokes — when to use each, what the blade is doing, and why the wrong choice leaves streaks.',
    strokes: [
      {
        id: 'fan',
        name: 'The Fan Stroke',
        italic: 'one continuous arc',
        rating: 'Pro standard',
        duration: 6500,
        description:
          'The hallmark of the trade. One unbroken motion from top corner to bottom corner — the blade never leaves the glass. The hand rotates through the curve so the rubber stays at a working angle.',
        notes: [
          ['Entry', 'Start in the top corner with the blade tilted ~30° off vertical, working edge first.'],
          ['Mid-stroke', 'As you cross the middle, your wrist pivots — the blade follows the curve, never the wrist leading it.'],
          ['Exit', 'Finish in the opposite bottom corner. The pane should be dry behind you. If not, you stopped.'],
        ],
        diagnostic: 'If you see horizontal lines, you broke the stroke. The fan is one motion or it is nothing.',
        path: pathFan,
      },
      {
        id: 'straight',
        name: 'The Straight Pull',
        italic: 'horizontal passes, lift between',
        rating: 'Reliable',
        duration: 7500,
        description:
          'The homeowner default — and a perfectly good technique when done right. Horizontal passes, top to bottom, lift the blade between strokes and wipe it on a clean cloth before the next pass.',
        notes: [
          ['Pass 1', 'Top edge, left to right, blade tilted slightly down so water runs to the floor edge of the rubber.'],
          ['Wipe', 'Lift, wipe the rubber on a clean microfiber, reset at the left.'],
          ['Overlap', 'Each pass overlaps the previous by about an inch. No gaps means no streaks.'],
        ],
        diagnostic: 'Horizontal streaks usually mean you skipped the wipe-between step. The rubber holds dirty water for one stroke before it starts smearing.',
        path: pathStraight,
      },
      {
        id: 's',
        name: 'The S-Stroke',
        italic: 'the bridge technique',
        rating: 'Beginner-friendly',
        duration: 5500,
        description:
          'The middle path between the straight pull and the full fan. One continuous S-shaped curve covers the whole pane — easier to learn than the fan, faster than the straight pull. The technique most working cleaners actually use day-to-day.',
        notes: [
          ['Top arc', 'Start top-left, sweep across the top third of the pane in a gentle curve down to the right edge.'],
          ['Crossover', 'Reverse direction in the middle — the S-curve is the secret. The blade angle reverses with the direction.'],
          ['Finish', 'Bottom arc mirrors the top, ending in the bottom-left corner. Detail the perimeter with a microfiber.'],
        ],
        diagnostic: 'The S-stroke fails when the crossover is too tight — a sharp turn lifts the blade off the glass. Loose, lazy curves are correct here.',
        path: pathS,
      },
    ],
  },
  {
    id: 'detail',
    number: '02',
    name: 'The Detail Stroke',
    italic: 'the work after the work',
    summary:
      'The microfiber pass that comes after the squeegee. This is where amateurs stop and professionals begin — the perimeter, the corners, the trim work that determines whether the window looks clean or just less dirty.',
    strokes: [
      {
        id: 'lperim',
        name: 'The L-Perimeter',
        italic: 'the four-sided seal',
        rating: 'The finisher',
        duration: 8500,
        description:
          'Tracing the perimeter with a folded microfiber, you absorb the water bead the squeegee left behind on the gasket and frame edges. Skip this and the bead runs back across the glass within five minutes, leaving a curtain of streaks where nothing was wrong before.',
        notes: [
          ['The fold', 'Quarter-fold the microfiber so you have eight clean faces available before refolding. One full perimeter uses one face.'],
          ['Direction', 'Always go top → right → bottom → left, in one continuous motion. Working backwards smears the bead instead of absorbing it.'],
          ['Pressure', 'Light contact. The fiber wicks water; you do not need to push. If the cloth saturates, rotate to a fresh face.'],
        ],
        diagnostic: 'The most common failure: drying the cloth out by detailing the glass instead of the edges. The pane is already dry — the detail stroke is for the perimeter, period.',
        path: pathLPerimeter,
      },
      {
        id: 'pinch',
        name: 'The Corner Pinch',
        italic: 'the 90° problem',
        rating: 'Specialty',
        duration: 5500,
        description:
          'Square corners are where dirt accumulates and where the squeegee blade cannot reach. The pinch is a slow, deliberate move: cloth folded to a point, pressed into the corner, drawn out along one edge then the other. Two diagonals, never circles.',
        notes: [
          ['The fold', 'Pinch the cloth into a corner-shaped point — index finger and thumb, no fancy fold.'],
          ['First diagonal', 'Press into the corner, drag out along the horizontal edge for an inch. Lift.'],
          ['Second diagonal', 'Press back into the same corner, drag out along the vertical edge. Lift.'],
        ],
        diagnostic: 'Circular motion in the corner is a common amateur mistake. It pushes dirt into the joint instead of pulling it out. Two straight diagonals, always.',
        path: pathCornerPinch,
      },
      {
        id: 'lapLift',
        name: 'The Lap & Lift',
        italic: 'face rotation in motion',
        rating: 'Pro habit',
        duration: 7500,
        description:
          'For wide top trims and long mullions. Each pass is a single horizontal sweep; at the end of each, the cloth lifts and rotates to a fresh face before the next pass. Done correctly, you can clean a 10-foot trim with one cloth without ever using a damp face on the glass.',
        notes: [
          ['Pass 1', 'Sweep left to right with face A. The cloth is dry on entry, slightly damp on exit.'],
          ['The flip', 'Lift, rotate the cloth 180° in your palm to expose face B. Walk back to the left.'],
          ['Pass 2', 'Sweep again with the fresh face. Repeat for as many passes as the trim requires — eight faces per fold.'],
        ],
        diagnostic: 'If your second pass leaves a wet line behind it, you reused a saturated face. The flip is non-negotiable, even for short runs.',
        path: pathLapLift,
      },
    ],
  },
  {
    id: 'razor',
    number: '03',
    name: 'The Razor Scrape',
    italic: 'the angle that does not scratch',
    summary:
      'The most dangerous technique in the trade. Done right, a razor blade lifts paint, sticker residue, and hardened mineral from glass with no trace. Done wrong — and the wrong is simply a blade angle 15 degrees too steep — it scratches the pane permanently. Three things to learn, in order: the angle, the direction, and the wet rule.',
    strokes: [
      {
        id: 'lowAngle',
        name: 'The Low-Angle Scrape',
        italic: 'twenty-five degrees, never more',
        rating: 'High-stakes',
        duration: 9000,
        description:
          'The canonical motion: blade held at 25–30 degrees off the glass, pushed in one direction only, lifted fully off the surface before resetting. Three forward strokes, three lifts, three resets — never a back-and-forth.',
        notes: [
          ['The angle', 'Twenty-five degrees is the working number. A nickel laid flat against the glass is roughly 5°; the blade should be about five times that. If you can see your hand in the reflection of the blade, the angle is too steep.'],
          ['The direction', 'Push, never pull, and never both. A two-way scrape grinds debris into the glass on the return stroke. One direction means one direction — even if you have to walk around the window to do it.'],
          ['The lift', 'Fully clear of the glass between strokes. Gliding the blade back across the pane while it is still in contact is the second most common cause of scratches.'],
        ],
        diagnostic: 'If you hear a faint sandpaper sound, stop immediately. That noise is the blade contacting dry glass with a particle trapped under it. The next thing you will see is a hairline scratch.',
        path: pathLowAngle,
      },
      {
        id: 'angleTest',
        name: 'The Angle Test',
        italic: 'twenty-five vs. eighty',
        rating: 'Diagnostic',
        duration: 12000,
        description:
          'A cycling demonstration of four blade angles — 25°, 45°, 60°, and 80° — with the verdict for each. Watch how the failure mode changes as the angle steepens. The first two scrape; the second two scratch. There is no in-between.',
        notes: [
          ['25 degrees', 'The working angle. Blade slides under deposits, lifts cleanly, leaves no mark. This is what professionals look like.'],
          ['45 degrees', 'The danger zone. Will still scrape softer deposits, but trapped grit now bears down on the glass. Risky on coated panes.'],
          ['60+ degrees', 'You are no longer scraping. You are pressing a sharp edge into the glass. This is how every "I just used a razor blade and now my window is ruined" story begins.'],
        ],
        diagnostic: 'Most homeowners hold a razor blade like they are peeling a vegetable — naturally near 60°. The wrist must be deliberately flattened. It feels wrong because it is unfamiliar; it is right because it works.',
        path: pathAngleTest,
      },
      {
        id: 'edgeApproach',
        name: 'The Edge Approach',
        italic: 'never start in the middle',
        rating: 'Specialty',
        duration: 8500,
        description:
          'When scraping a discrete deposit — paint speck, sticker residue, hardened bird droppings — never enter from the middle. The blade approaches from outside the deposit and slides under its edge tangentially, lifting it off the glass like a spatula under a pancake.',
        notes: [
          ['Approach', 'Bring the blade in from the side, parallel to the glass, with the deposit ahead of you. Do not stab.'],
          ['Slide under', 'Contact the edge of the deposit, not the top. The blade goes between the deposit and the glass.'],
          ['Lift away', 'Once the deposit releases, lift the blade away from the surface immediately. Do not continue past the deposit — that is unnecessary scraping on clean glass.'],
        ],
        diagnostic: 'Scraping from the middle outward forces the blade to break through the deposit, which means it has to push down on the glass to do so. The edge approach uses the deposit\'s own bond to the glass to wedge the blade under it.',
        path: pathEdgeApproach,
      },
    ],
  },
  {
    id: 'application',
    number: '04',
    name: 'The Application Pass',
    italic: 'before the squeegee, the wash',
    summary:
      'Solution does not magically arrive on the glass — somebody has to put it there, and how they put it there determines whether the squeegee even has a chance. Three application methods, each with a working purpose. The sleeve for production work, the sea sponge for craft, and the spray-and-spread for everyone else.',
    strokes: [
      {
        id: 'sleeveWash',
        name: 'The Sleeve Wash',
        italic: 'load, prime, fill',
        rating: 'Pro standard',
        duration: 11000,
        description:
          'A microfiber sleeve over a T-bar handle, dipped in a bucket of solution, applied to glass in a deliberate three-phase motion: load, edge-prime, fill. The whole pane is wetted in under thirty seconds, and the squeegee that follows has a uniform film to clear instead of a patchwork.',
        notes: [
          ['Load', 'Submerge the sleeve fully, give it three seconds to saturate, lift, and squeeze the handle once against the rim of the bucket. You want it heavy with solution, not dripping.'],
          ['Edge-prime', 'Run the sleeve along the top edge first, then down the sides. This locks the perimeter — the area where the squeegee will lose water if it is dry — before you fill the middle.'],
          ['Fill', 'Top-to-bottom passes in three overlapping columns. The sleeve never lifts during the fill phase. The whole pane should be evenly wet, no dry spots.'],
        ],
        diagnostic: 'If your squeegee leaves trails on the first pass, your sleeve was too dry. If it floods the windowsill, the sleeve was too wet. The squeeze-once-on-the-rim is calibration, not optional.',
        path: pathSleeveWash,
      },
      {
        id: 'spongeDab',
        name: 'The Sea Sponge Dab',
        italic: 'the older way, still the best on textured glass',
        rating: 'Specialty',
        duration: 14000,
        description:
          'Before microfiber, there was the natural sea sponge. The technique is fundamentally different: instead of dragging solution across the glass, you press it into the surface in an overlapping grid of dabs. Slower, more deliberate, and unmatched on textured or seeded glass where a flat sleeve skips the recessed areas.',
        notes: [
          ['The grid', 'Imagine a five-by-four grid on the pane. Each cell gets one dab. Do not freelance — the grid prevents missed spots, which on textured glass means visible streaks.'],
          ['The press', 'Press straight down with steady pressure for half a second. The natural cells of the sponge fill the texture of the glass; a sleeve cannot do this.'],
          ['The lift', 'Lift straight up. Do not slide. Sliding compresses the sponge and squeezes solution out unevenly, leaving a heavy spot you will see ten minutes later.'],
        ],
        diagnostic: 'Synthetic kitchen sponges do not work for this technique — the cells are too uniform and too closed. A genuine natural sea sponge is the tool. Yes, they are expensive. Yes, they last for years.',
        path: pathSpongeDab,
      },
      {
        id: 'spraySpread',
        name: 'The Spray & Spread',
        italic: 'the homeowner default, done right',
        rating: 'Common',
        duration: 12000,
        description:
          'The technique most people use — spray bottle to glass, microfiber to spread — is genuinely fine for residential work, provided you do two things almost no one does. First, mist five spots, not one big puddle. Second, spread with a folded clean cloth before the solution dries.',
        notes: [
          ['Five spots, not one', 'A single big spray runs down the glass before you can spread it, leaving the bottom over-soaked and the top already dry. Five small mists keep the solution where you put it.'],
          ['Spread top-down', 'Begin spreading at the top — gravity carries solution downward, so the bottom mists are already getting help. Working bottom-up means doing the same work twice.'],
          ['Speed matters', 'Solution evaporates in under sixty seconds on a sunny day. Spray, spread, squeegee or wipe-dry within that window or you are cleaning over dried film, which is the streak you will see at the end.'],
        ],
        diagnostic: 'The single biggest mistake here is spraying too much. The solution wants to be a thin film, not a puddle. If you can see liquid running, you are using twice as much as you need.',
        path: pathSprayAndSpread,
      },
    ],
  },
  {
    id: 'channelWipe',
    number: '05',
    name: 'The Channel Wipe',
    italic: 'the move between strokes',
    summary:
      'The least glamorous technique in the trade, and the one that separates a clean window from one with horizontal streaks across it. After every squeegee pass the rubber holds dirty water; if you do not remove it, the next pass smears it back across the glass. Three ways pros do this — ranked by speed, ranked by quality.',
    strokes: [
      {
        id: 'crossBody',
        name: 'The Cross-Body Wipe',
        italic: 'cloth in the off-hand',
        rating: 'Quality standard',
        duration: 13000,
        description:
          'A folded microfiber held permanently in the non-dominant hand, low and to the side. After every downstroke on the pane, the squeegee crosses the body, drags down the cloth, flips, drags up the cloth, and returns. Two passes per wipe means both faces of the rubber get cleaned every time. The slowest method, and the most thorough.',
        notes: [
          ['The cloth position', 'Held at hip height, slightly out from the body, palm up. The cloth never moves; the squeegee comes to it. Stationary tool, moving tool — never both.'],
          ['Two passes per wipe', 'First pass: blade drags down across the cloth. Flip the squeegee 180°. Second pass: drag up across a fresh part of the cloth. Both rubber faces are now clean.'],
          ['The fold rotates', 'Quarter-fold the cloth before starting. Every 8–10 wipes, refold to expose a fresh quadrant. A saturated cloth recontaminates the blade — the entire point is defeated.'],
        ],
        diagnostic: 'If you find yourself wiping the cloth on your shirt, the cloth is saturated and you are now smearing dirty cloth water back onto the blade. Unfold to a fresh face or change cloths.',
        path: pathCrossBodyWipe,
      },
      {
        id: 'thigh',
        name: 'The Thigh Wipe',
        italic: 'fast, production grade',
        rating: 'Production',
        duration: 11000,
        description:
          'Cloth tucked into the front of the apron or belt, hanging vertically. Single quick downward swipe of the blade across it after each stroke. Half the motion of the cross-body wipe, half the time. Used on commercial routes where speed determines wage.',
        notes: [
          ['The tuck', 'Fold cloth into a long rectangle, tuck top edge into apron pocket so most of the cloth hangs free. The free end is what you wipe on.'],
          ['One pass, one face', 'Single downward drag across the cloth. Only one rubber face gets cleaned. The other side comes around on the next squeegee stroke and gets wiped on the return.'],
          ['Replace often', 'Production cleaners burn through one cloth every 2–3 hours. The cloth is consumable, like a brake pad — if you are not replacing it, you are smearing.'],
        ],
        diagnostic: 'If you wipe the same face twice in a row by accident, the second wipe puts dirt back on the blade. Establish a rhythm: stroke, wipe, stroke, flip-and-wipe, stroke, wipe.',
        path: pathThighWipe,
      },
      {
        id: 'bucketRim',
        name: 'The Bucket-Edge Wipe',
        italic: 'the compromise',
        rating: 'Conditional',
        duration: 10000,
        description:
          'When the cloth is across the room, on the truck, or in the wash, drag the blade along the rim of the solution bucket. The rim physically shears water off the rubber. It is not as clean as a microfiber wipe, but it is faster than walking to the truck — and infinitely better than skipping the wipe.',
        notes: [
          ['The angle', 'Hold the blade nearly perpendicular to the rim, tilted forward about 10°. Too flat and the water runs back over the blade; too steep and you risk scratching the rubber on the rim.'],
          ['One pass only', 'Drag the entire length of the rubber across the rim once. Going back and forth pushes shearings back into the blade.'],
          ['Last resort', 'This is documented because it is real, not because it is recommended. A clean cloth will always outperform a wet bucket rim.'],
        ],
        diagnostic: 'If the bucket rim has soap scum on it from earlier wipes, you are now applying that scum back to the rubber. Wipe the rim itself with a cloth between cleanings, or accept a quality penalty.',
        path: pathBucketEdgeWipe,
      },
    ],
  },
  {
    id: 'bucketSetup',
    number: '06',
    name: 'The Bucket Setup',
    italic: 'workflow before motion',
    summary:
      'Everything before the first stroke is the bucket setup. How you arrange the buckets, how you load the sleeve, and where you place every piece of equipment determines whether your technique has a chance. The pros who look fast at the pane are actually fast at this.',
    strokes: [
      {
        id: 'twoBucket',
        name: 'The Two-Bucket System',
        italic: 'wash and rinse, separated',
        rating: 'Pro standard',
        duration: 16000,
        description:
          'Two buckets, side by side. The wash bucket holds your soap solution. The rinse bucket holds plain water. After every pane (or every few panes on heavy work), the sleeve cycles: dirty wash water gets rinsed out in the rinse bucket before it returns to the wash bucket and contaminates the whole batch. The single-bucket system that homeowners use turns dirty in twenty minutes; the two-bucket lasts hours.',
        notes: [
          ['Wash on the dominant side', 'For right-handed cleaners, wash bucket on the right, rinse on the left. The dominant hand handles the squeegee; the off-hand cycles the sleeve. The geometry matters when you do this 200 times in a day.'],
          ['Rinse first when in doubt', 'If the sleeve picks up grit on the pane, the rinse bucket strips it before it goes back into the wash. The wash water stays clean longer, the streak risk drops, the day gets faster.'],
          ['Refresh the rinse', 'When the rinse bucket water turns visibly cloudy, dump it and refill. The wash bucket can usually outlast it 3:1 — the rinse is doing the harder work.'],
        ],
        diagnostic: 'A single-bucket workflow on a job over thirty minutes long is how you get streaks that nothing else explains. The wash water has been turning into rinse water in slow motion the whole time.',
        path: pathTwoBucket,
      },
      {
        id: 'sleeveLoad',
        name: 'The Sleeve Loading',
        italic: 'submerge, twist, lift, squeeze',
        rating: 'Foundational',
        duration: 12000,
        description:
          'Four steps, in order, every load. Submerge fully — three seconds, no shortcut. Twist underwater to release any air pockets in the microfiber. Lift straight up. Squeeze once along the rim, calibrating the saturation level. The whole thing takes under five seconds when learned, and almost none of the homeowners who have ever held a sleeve do all four steps.',
        notes: [
          ['Submerge fully', 'Three seconds. Air trapped in the microfiber means the inner cells never wet out, which means the sleeve runs out of solution mid-pane and you wonder why the bottom looks streaky.'],
          ['Twist underwater', 'Quarter-turn either direction. The motion releases trapped air bubbles you can see rising. Synthetic sleeves trap less air than wool ones, but the twist still helps.'],
          ['Squeeze on the rim', 'Single firm drag along the bucket rim with the sleeve flat against it. This is how you calibrate saturation — too dry on first stroke means add a half-second to the rim drag next time; too wet means add a full second.'],
        ],
        diagnostic: 'A sleeve dipped, lifted, and immediately used will give you a sopping-wet first pane and a bone-dry third pane. The four steps are not a ritual — they are calibration.',
        path: pathSleeveLoad,
      },
      {
        id: 'toolLayout',
        name: 'The Tool Layout',
        italic: 'a place for everything',
        rating: 'Production',
        duration: 14000,
        description:
          'The canonical layout of a working window cleaner. Buckets on the floor, squeegee and scraper in dedicated holsters or apron loops, cloths within arm reach but never on the floor, apron tied. Items appear one at a time in their working positions. There is a reason every working pro arranges the same way: it is the spatial memory that lets you stop thinking about where things are.',
        notes: [
          ['Buckets at the wall', 'Both buckets pushed against the wall under the window, never in front of you. Stepping back from the pane to evaluate your work means stepping into open floor, not into a bucket.'],
          ['Tools on your body', 'Squeegee, scraper, and at least one cloth on the apron at all times. Reaching for a tool on the floor breaks rhythm and adds a dozen seconds per window — meaningful on a 40-window day.'],
          ['Cloths in pairs', 'One on the apron for active wiping, one folded in the back pocket as a reserve. Single-cloth setups always end in a bad cloth being used past saturation.'],
        ],
        diagnostic: 'A messy layout is the single biggest predictor of how the windows will look. Pros who arrive at a job and immediately arrange equipment before unscrewing the lid on the cleaner are the ones whose work you cannot fault.',
        path: pathToolLayout,
      },
    ],
  },
  {
    id: 'tiltIn',
    number: '07',
    name: 'The Tilt-In Reach',
    italic: 'cleaning the outside from inside',
    summary:
      'Modern double-hung windows have tilt-in sashes specifically so you can clean both sides of the glass from inside the home. The technique is mostly about controlling the sash — releasing the latches in the right order, supporting the weight as it pivots, and re-seating it without tearing the gaskets. Done properly, the second-floor outside surfaces become as accessible as the inside ones. Done improperly, you end up with a dropped sash and a window that no longer closes.',
    strokes: [
      {
        id: 'sashTilt',
        name: 'The Sash Tilt-In',
        italic: 'release, release, lower',
        rating: 'Foundational',
        duration: 11000,
        description:
          'Release the left latch first, then the right, then control the sash as it pivots inward under its own weight. Both hands stay on the sash from the moment the second latch releases until the sash is fully tilted. Letting it free-fall — even an inch — risks bending the balance springs that hold the sash in track.',
        notes: [
          ['Left first', 'Convention is to release the left tilt latch before the right. The sash will not pivot until both are free, so order matters less for safety than for habit, but habit is what keeps you from skipping the second latch on a tired afternoon.'],
          ['Both hands on, immediately', 'The instant the second latch releases, both hands take the top rail. The sash will pivot the moment it has permission to. Surprise drops are how broken windows happen.'],
          ['Lower under control', 'Tilt slowly. The sash should reach 45° in about three seconds, not three-tenths. Faster and the balance springs whip; slower and your wrists give up before you finish the cycle.'],
        ],
        diagnostic: 'If the sash will not pivot inward after both latches release, do not force it. Many older units have paint sealing the joint — cut along the joint with a utility blade before retrying, or you will tear the gasket.',
        path: pathSashTiltIn,
      },
      {
        id: 'reachAcross',
        name: 'The Reach-Across',
        italic: 'lean, do not extend',
        rating: 'Safety-critical',
        duration: 9000,
        description:
          'With the sash tilted in to 45°, the outer face of the glass is now angled toward you, accessible without leaving the room. The squeegee technique itself is a normal S-stroke — what changes is the body position. Your weight stays over your feet, your forearm does the reaching, and you never lock your knees against the windowsill to extend further than your arms allow.',
        notes: [
          ['Forearm reach only', 'The shoulder stays inside the room. If your shoulder crosses the plane of the window, you have leaned too far and your center of gravity is approaching the sill.'],
          ['Both feet flat', 'Both feet remain in firm contact with the floor. One foot lifted to extend reach is how second-floor accidents start. If you cannot reach a corner with both feet flat, the sash needs to tilt further or the technique does not apply.'],
          ['Squeegee, then check', 'Complete one pass, lean back, evaluate, then resume. Working continuously without resetting your stance is how fatigue compounds into mistakes.'],
        ],
        diagnostic: 'A safety harness is overkill on a residential second-story job. A flat-footed stance and a sensible reach envelope are not. The single biggest predictor of falls is windows that need cleaning above what feels comfortable to reach.',
        path: pathReachAcross,
      },
      {
        id: 'reSeat',
        name: 'The Re-Seat',
        italic: 'wet sash, reverse the order',
        rating: 'Foundational',
        duration: 11000,
        description:
          'Lifting the sash back into vertical is the harder half of the technique. The sash is now wet, marginally heavier, and the gaskets are slick — the latches have to slide into seated position cleanly or they will fail to engage. Lift to vertical, hold against the frame, engage the right latch, then the left.',
        notes: [
          ['Lift before locking', 'The sash must be fully vertical and pressed flush against the frame before either latch can engage. Trying to lock with a half-degree gap will spring the latch and require a second attempt.'],
          ['Right latch first this time', 'Reverse the release order on re-seat. Lock the side opposite where you started. This balances the seating force and prevents the sash from sliding sideways out of true.'],
          ['Test the lock', 'Pull gently down on the top rail after both latches engage. Movement means the latches did not seat — repeat the procedure. A locked sash does not move.'],
        ],
        diagnostic: 'If the latches refuse to engage after re-seating, the issue is almost always alignment. Tilt the sash back out a quarter-inch and re-seat — the second attempt usually catches.',
        path: pathReSeat,
      },
    ],
  },
  {
    id: 'pole',
    number: '08',
    name: 'The Pole Technique',
    italic: 'water-fed work at height',
    summary:
      'A 30-foot carbon-fiber pole, a brush head with embedded jets, and a feed of pure deionized water from a tank or filter. This is how every commercial high-rise gets cleaned, and increasingly how second-story residential gets done too — no ladders, no harnesses, no liability. The technique is fundamentally different from ground-level work: there is no squeegee at all. The pure water has nothing in it to leave behind, so it dries to nothing.',
    strokes: [
      {
        id: 'lowAnglePole',
        name: 'The Low-Angle Pole',
        italic: 'small base, big head',
        rating: 'Foundational',
        duration: 13000,
        description:
          'The single hardest thing about pole work is that everything is amplified. A two-inch movement at your hands becomes a ten-inch movement at the head. The teaching is to move less, not more — and to expect your first hour with a pole to feel like flailing. Three demonstration sweeps show what wobbly, drifting, and corrected motion look like at the head.',
        notes: [
          ['Hands close to body', 'Both hands grip the pole near your hips, not extended forward. Extending your hands forward only makes the leverage problem worse and tires your shoulders inside ten minutes.'],
          ['Move from the legs', 'Side-to-side motion on the pane comes from a small step or weight shift, not from sweeping your arms. Arms are for holding; legs are for moving.'],
          ['Underbalance, then correct', 'A pole at full extension will drift in the direction it is leaning. Anticipate the drift before it happens — start the next stroke a little behind where you want to end up, and let the pole settle into position.'],
        ],
        diagnostic: 'If your strokes are leaving wavy lines instead of straight ones, the wobble is in your hands, not the pole. The fix is gripping less tightly, not more — a death grip transmits every tremor in your arms straight to the head.',
        path: pathLowAnglePole,
      },
      {
        id: 'twoStorySweep',
        name: 'The Two-Story Sweep',
        italic: 'scrub, rinse, let it dry',
        rating: 'Pro standard',
        duration: 14000,
        description:
          'The complete pole-cleaning workflow on a single pane: scrub with bristles to lift soil, rinse with the pure water jets to flush it away, then walk the pole off the pane and let gravity do the drying. There is no squeegee, no microfiber, no detail pass. Pure water dries to nothing because there is nothing in it. Two panes shown, in sequence.',
        notes: [
          ['Scrub first, top-down', 'Bristles work the surface in rapid back-and-forth passes, walking down the pane. Loosened soil flushes to the bottom edge. The whole pane gets agitated before any rinse begins.'],
          ['Rinse top-down too', 'Switch to the jets and pull straight down in three or four overlapping vertical passes. Gravity carries the rinse water down the pane, taking soil with it. Rinsing upward fights gravity and never finishes the job.'],
          ['Walk off the side', 'Final move: drag the brush horizontally off the side of the pane. This breaks the water bead at the edge so it cannot run back across the dry pane. Then leave it. Pure water dries clear in three to five minutes.'],
        ],
        diagnostic: 'If you see streaks after a pole job, your water is not pure. A working DI filter or RO system reads under 10 ppm TDS at the brush head; anything higher and you will see ghost streaks no matter how perfect your technique.',
        path: pathTwoStorySweep,
      },
      {
        id: 'brushAndRinse',
        name: 'The Brush & Rinse',
        italic: 'two functions, one head',
        rating: 'Specialty',
        duration: 12000,
        description:
          'Modern pole brush heads combine bristles for agitation and jets for rinsing in a single tool. The teaching is the rhythm — alternating between bristle-dominant scrub motions (small, circular, soil-lifting) and jet-dominant rinse motions (long, straight, soil-flushing). Five alternating segments demonstrate the canonical sequence.',
        notes: [
          ['Bristles agitate', 'When scrubbing, the jets should still flow but the motion is small and circular. The bristles do the lifting; the jets keep the surface from drying mid-scrub.'],
          ['Jets flush', 'When rinsing, the bristles still touch the glass but the motion is long and slow. The jets do the work; the bristles are along for the ride.'],
          ['Always end on rinse', 'The last motion on any pane is always a slow vertical rinse pass. End on a scrub and you leave bristle marks in the water film as it dries.'],
        ],
        diagnostic: 'A jet flow that visibly diminishes mid-job means your tank is empty, your filter is clogged, or your pump is failing. Stop and diagnose — finishing the pane with weak flow guarantees rework.',
        path: pathBrushAndRinse,
      },
    ],
  },
];

// ═════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════
export default function TechniqueLibrary() {
  const [chapterId, setChapterId] = useState('squeegee');
  const [strokeId, setStrokeId] = useState('fan');
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [dark, setDark] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [view, setView] = useState('library'); // 'library' | 'chapter'
  const rafRef = useRef(null);
  const lastFrameRef = useRef(null);

  const chapter = CHAPTERS.find((c) => c.id === chapterId);
  const stroke = chapter?.strokes.find((s) => s.id === strokeId) ?? chapter.strokes[0];

  // Reset to first stroke when chapter changes
  useEffect(() => {
    setStrokeId(chapter.strokes[0].id);
  }, [chapterId]);

  useEffect(() => {
    setT(0);
    lastFrameRef.current = null;
  }, [strokeId, chapterId]);

  // Animation loop
  useEffect(() => {
    if (!playing || view !== 'chapter') {
      lastFrameRef.current = null;
      return;
    }
    const tick = (now) => {
      if (lastFrameRef.current == null) lastFrameRef.current = now;
      const dt = now - lastFrameRef.current;
      lastFrameRef.current = now;
      setT((prev) => {
        const next = prev + (dt * speed) / stroke.duration;
        return next >= 1 ? 0 : next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastFrameRef.current = null;
    };
  }, [playing, speed, stroke.duration, view]);

  // Sample trail points
  const trailPoints = useMemo(() => {
    const samples = 100;
    return Array.from({ length: samples + 1 }, (_, i) => {
      const tt = i / samples;
      return { ...stroke.path(tt), t: tt };
    });
  }, [stroke]);

  const current = stroke.path(t);
  // Trail points used for cleaning the pane:
  //   - must be sampled by now (p.t <= t)
  //   - must not be lifted off the surface
  //   - must not be explicitly off-pane (channel-wipe travel/wipe segments)
  const visibleTrail = trailPoints.filter((p) => p.t <= t && !p.lifted && p.onPane !== false);

  // Theme tokens
  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
        glass: '#2A2F33', glassEdge: '#3A4045',
        dirty: 'rgba(184, 178, 164, 0.12)',
        clean: 'rgba(244, 241, 234, 0.04)',
        trail: '#9CA89A',
        cloth: '#9CA89A',
        clothAlt: '#7E8C7C',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
        glass: '#E8E4D8', glassEdge: '#C4BFAE',
        dirty: 'rgba(122, 116, 102, 0.18)',
        clean: 'rgba(255, 253, 247, 0.6)',
        trail: '#9CA89A',
        cloth: '#9CA89A',
        clothAlt: '#7E8C7C',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <StaticSeo schemaType="webApplication" path="/tools/technique-library" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        @keyframes pinchPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(0.85); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }

        .chapter-tile {
          transition: background 0.2s, transform 0.2s;
          cursor: pointer;
        }
        .chapter-tile:hover {
          background: ${T.surface};
        }
        .chapter-tile:hover .arrow { transform: translateX(4px); }
        .arrow { transition: transform 0.2s; display: inline-block; }

        .stroke-tab { transition: background 0.2s, color 0.2s, border-color 0.2s; }
        .stroke-tab.active { border-bottom-color: ${T.sienna} !important; }
        .stroke-tab:hover { color: ${T.ink}; }

        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }
        .ghost-btn:hover { color: ${T.sienna}; }
        .icon-btn:hover { background: ${T.surface}; }

        .scrub-track {
          position: relative;
          height: 2px;
          background: ${T.rule};
          cursor: pointer;
        }
        .scrub-fill {
          position: absolute;
          top: 0; left: 0; height: 100%;
          background: ${T.sienna};
          pointer-events: none;
        }
        .scrub-handle {
          position: absolute;
          top: 50%;
          width: 12px; height: 12px;
          background: ${T.ink};
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }

        @media (max-width: 820px) {
          .grid-2col { grid-template-columns: 1fr !important; }
          .h1-title { font-size: 38px !important; line-height: 1.05 !important; }
          .chapter-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* ─── Header ──────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            paddingBottom: 20,
            borderBottom: `1px solid ${T.rule}`,
            marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            aria-label="Toggle dark mode"
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* LIBRARY VIEW                                             */}
        {/* ═══════════════════════════════════════════════════════ */}
        {view === 'library' && (
          <>
            <div className="reveal reveal-1">
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 20,
                }}
              >
                § THE TOOLS &nbsp;·&nbsp; 04 / 09 &nbsp;·&nbsp; ANIMATED
              </div>

              <h1
                className="h1-title"
                style={{
                  fontFamily: fontDisplay, fontSize: 60, fontWeight: 400,
                  lineHeight: 1.04, letterSpacing: '-0.025em',
                  margin: '0 0 24px', maxWidth: 800,
                }}
              >
                The Technique{' '}
                <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
                  Library
                </em>
                .
              </h1>

              <p
                style={{
                  fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
                  lineHeight: 1.5, color: T.inkSoft,
                  maxWidth: 680, margin: '0 0 12px',
                }}
              >
                Every motion in the trade, animated. The squeegee strokes, the
                detail work, the corner pinch — the things static diagrams can't
                teach. Pause, scrub, slow it down. Study the wrist.
              </p>

              <p
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
                }}
              >
                Animations documented from working cleaners. Updated as new chapters are filmed.
              </p>
            </div>

            {/* Chapter list */}
            <div
              className="chapter-grid reveal reveal-2"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 0,
                borderTop: `1px solid ${T.rule}`,
                marginBottom: 56,
              }}
            >
              {CHAPTERS.map((c, idx) => (
                <button
                  key={c.id}
                  className="chapter-tile"
                  onClick={() => { setChapterId(c.id); setView('chapter'); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderRight: idx % 2 === 0 ? `1px solid ${T.rule}` : 'none',
                    borderBottom: `1px solid ${T.rule}`,
                    padding: '36px 32px 36px 0',
                    paddingLeft: idx % 2 === 1 ? 32 : 0,
                    textAlign: 'left',
                    color: T.ink,
                    fontFamily: fontBody,
                    minHeight: 240,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: fontMono, fontSize: 11,
                        letterSpacing: '0.18em', color: T.sienna,
                        marginBottom: 14,
                      }}
                    >
                      CHAPTER {c.number} &nbsp;·&nbsp; {c.strokes.length} ANIMATIONS
                    </div>
                    <h2
                      style={{
                        fontFamily: fontDisplay, fontSize: 32, fontWeight: 400,
                        lineHeight: 1.1, letterSpacing: '-0.02em',
                        margin: '0 0 6px',
                      }}
                    >
                      {c.name}
                    </h2>
                    <div
                      style={{
                        fontFamily: fontDisplay, fontStyle: 'italic',
                        fontSize: 17, color: T.sienna,
                        marginBottom: 14,
                      }}
                    >
                      {c.italic}
                    </div>
                    <p
                      style={{
                        fontFamily: fontDisplay, fontSize: 16, fontWeight: 300,
                        lineHeight: 1.55, color: T.inkSoft,
                        margin: '0 0 16px',
                      }}
                    >
                      {c.summary}
                    </p>
                  </div>
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', color: T.sienna,
                    }}
                  >
                    OPEN CHAPTER <span className="arrow">→</span>
                  </div>
                </button>
              ))}

              {/* Coming soon tiles */}
              {[
                ['09', 'The Track Clean', 'window tracks & sashes'],
                ['10', 'The Cobweb Sweep', 'soffit & frame work'],
                ['11', 'The Storm Window', 'glass + screen + storm'],
                ['12', 'The Skylight Reach', 'overhead glass at angle'],
              ].map(([num, name, ital], i) => {
                // Combined grid position so borders align across chapter + forthcoming
                const globalIdx = CHAPTERS.length + i;
                const inLeftCol = globalIdx % 2 === 0;
                return (
                <div
                  key={num}
                  style={{
                    background: 'transparent',
                    borderRight: inLeftCol ? `1px solid ${T.rule}` : 'none',
                    borderBottom: `1px solid ${T.rule}`,
                    padding: '36px 32px 36px 0',
                    paddingLeft: inLeftCol ? 0 : 32,
                    color: T.inkFaint,
                    minHeight: 180,
                    opacity: 0.55,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', marginBottom: 14,
                    }}
                  >
                    CHAPTER {num} &nbsp;·&nbsp; FORTHCOMING
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontSize: 26, fontWeight: 400,
                      lineHeight: 1.1, letterSpacing: '-0.02em',
                      marginBottom: 4,
                    }}
                  >
                    {name}
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontStyle: 'italic',
                      fontSize: 15,
                    }}
                  >
                    {ital}
                  </div>
                </div>
                );
              })}
            </div>

            {/* Cross-links from library view */}
            <div
              className="reveal reveal-3"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 0,
                borderTop: `1px solid ${T.rule}`,
                marginBottom: 40,
              }}
            >
              {[
                ['DIAGNOSTICIAN', 'Why your windows look worse after you clean them', '/articles/why-windows-look-worse'],
                ['THE TOOLS', 'The Solution Calculator →', '/tools/solution-calculator'],
                ['PRO TRACK', 'The squeegee: a working buyer\'s guide', '/articles/squeegee-anatomy-technique-rubber'],
              ].map(([kicker, title, to]) => (
                <Link
                  key={title}
                  to={to}
                  className="ghost-btn"
                  style={{
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none',
                    color: T.ink,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 8,
                    }}
                  >
                    {kicker}
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontSize: 17,
                      lineHeight: 1.3, paddingRight: 16,
                    }}
                  >
                    {title}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* CHAPTER VIEW                                             */}
        {/* ═══════════════════════════════════════════════════════ */}
        {view === 'chapter' && (
          <>
            <div className="reveal reveal-1">
              <button
                onClick={() => setView('library')}
                className="ghost-btn"
                style={{
                  background: 'transparent', border: 'none',
                  color: T.inkSoft,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  cursor: 'pointer', padding: 0,
                  marginBottom: 24,
                }}
              >
                ← THE TECHNIQUE LIBRARY
              </button>

              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                CHAPTER {chapter.number} &nbsp;·&nbsp; ANIMATED &nbsp;·&nbsp; {chapter.strokes.length} TECHNIQUES
              </div>

              <h1
                className="h1-title"
                style={{
                  fontFamily: fontDisplay, fontSize: 52, fontWeight: 400,
                  lineHeight: 1.04, letterSpacing: '-0.025em',
                  margin: '0 0 6px', maxWidth: 800,
                }}
              >
                {chapter.name}
              </h1>

              <div
                style={{
                  fontFamily: fontDisplay, fontStyle: 'italic',
                  fontSize: 22, color: T.sienna, fontWeight: 400,
                  marginBottom: 20,
                }}
              >
                {chapter.italic}
              </div>

              <p
                style={{
                  fontFamily: fontDisplay, fontSize: 19, fontWeight: 300,
                  lineHeight: 1.5, color: T.inkSoft,
                  maxWidth: 680, margin: '0 0 48px',
                }}
              >
                {chapter.summary}
              </p>
            </div>

            {/* Stroke selector tabs */}
            <div
              className="reveal reveal-2"
              style={{
                display: 'flex',
                gap: 0,
                borderBottom: `1px solid ${T.rule}`,
                marginBottom: 40,
                overflowX: 'auto',
              }}
            >
              {chapter.strokes.map((s, i) => (
                <button
                  key={s.id}
                  className={`stroke-tab ${strokeId === s.id ? 'active' : ''}`}
                  onClick={() => setStrokeId(s.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '2px solid transparent',
                    padding: '16px 28px 18px 0',
                    marginRight: 32,
                    cursor: 'pointer',
                    color: strokeId === s.id ? T.ink : T.inkSoft,
                    fontFamily: fontBody,
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    marginBottom: -1,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.15em',
                      color: strokeId === s.id ? T.sienna : T.inkFaint,
                      marginBottom: 6,
                    }}
                  >
                    № 0{i + 1}
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontSize: 20,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {s.name}
                  </div>
                </button>
              ))}
            </div>

            {/* Two-column animation + docs */}
            <div
              className="grid-2col reveal reveal-3"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 360px',
                gap: 48,
                marginBottom: 56,
              }}
            >
              {/* LEFT: animated pane */}
              <div>
                <AnimatedPane
                  stroke={stroke}
                  current={current}
                  visibleTrail={visibleTrail}
                  trailPoints={trailPoints}
                  showGrid={showGrid}
                  t={t}
                  T={T}
                />

                {/* Playback controls */}
                <div
                  style={{
                    marginTop: 24,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    className="icon-btn"
                    onClick={() => setPlaying(!playing)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.rule}`,
                      color: T.ink,
                      width: 44, height: 44,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                    }}
                    aria-label={playing ? 'Pause' : 'Play'}
                  >
                    {playing ? '❚❚' : '▶'}
                  </button>

                  <button
                    className="icon-btn"
                    onClick={() => { setT(0); setPlaying(true); }}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.rule}`,
                      color: T.ink,
                      width: 44, height: 44,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                    }}
                    aria-label="Restart"
                  >
                    ↺
                  </button>

                  <div
                    className="scrub-track"
                    style={{ flex: 1, minWidth: 160 }}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const newT = (e.clientX - rect.left) / rect.width;
                      setT(Math.max(0, Math.min(1, newT)));
                      setPlaying(false);
                    }}
                  >
                    <div className="scrub-fill" style={{ width: `${t * 100}%` }} />
                    <div className="scrub-handle" style={{ left: `${t * 100}%` }} />
                  </div>

                  <div style={{ display: 'flex', gap: 0 }}>
                    {[0.5, 1, 2].map((s) => (
                      <button
                        key={s}
                        onClick={() => setSpeed(s)}
                        style={{
                          background: speed === s ? T.ink : 'transparent',
                          color: speed === s ? T.bg : T.inkSoft,
                          border: `1px solid ${T.rule}`,
                          borderLeft: 'none',
                          fontFamily: fontMono,
                          fontSize: 10,
                          letterSpacing: '0.1em',
                          padding: '12px 12px',
                          cursor: 'pointer',
                          minWidth: 44,
                        }}
                      >
                        {s}×
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontFamily: fontMono,
                    fontSize: 10,
                    letterSpacing: '0.1em',
                    color: T.inkFaint,
                  }}
                >
                  <button
                    onClick={() => setShowGrid(!showGrid)}
                    className="ghost-btn"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: T.inkFaint,
                      fontFamily: fontMono,
                      fontSize: 10,
                      letterSpacing: '0.15em',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {showGrid ? '✓ ' : '◯ '}REFLECTION GRID
                  </button>
                  <span>
                    {Math.round((stroke.duration / 1000) * 10) / 10}S{speed !== 1 && ` ÷ ${speed} = ${Math.round((stroke.duration / 1000 / speed) * 10) / 10}S`}
                  </span>
                </div>
              </div>

              {/* RIGHT: documentation */}
              <div>
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 16,
                  }}
                >
                  ◆ {stroke.rating.toUpperCase()}
                </div>

                <h2
                  style={{
                    fontFamily: fontDisplay, fontSize: 30, fontWeight: 400,
                    lineHeight: 1.1, letterSpacing: '-0.025em',
                    margin: '0 0 6px',
                  }}
                >
                  {stroke.name}
                </h2>

                <div
                  style={{
                    fontFamily: fontDisplay, fontStyle: 'italic',
                    fontSize: 16, color: T.sienna,
                    marginBottom: 20,
                  }}
                >
                  {stroke.italic}
                </div>

                <p
                  style={{
                    fontFamily: fontDisplay, fontSize: 15, fontWeight: 300,
                    lineHeight: 1.6, color: T.inkSoft,
                    margin: '0 0 28px',
                  }}
                >
                  {stroke.description}
                </p>

                <div style={{ borderTop: `1px solid ${T.rule}` }}>
                  {stroke.notes.map(([k, v], i) => (
                    <div
                      key={k}
                      style={{
                        padding: '14px 0',
                        borderBottom: `1px solid ${T.rule}`,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: fontMono, fontSize: 10,
                          letterSpacing: '0.15em', color: T.inkFaint,
                          marginBottom: 6,
                        }}
                      >
                        № 0{i + 1} &nbsp;·&nbsp; {k.toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontFamily: fontBody, fontSize: 14,
                          color: T.ink, lineHeight: 1.55,
                        }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 24,
                    background: T.surface,
                    borderLeft: `2px solid ${T.sienna}`,
                    padding: '16px 20px',
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 8,
                    }}
                  >
                    ⚡ DIAGNOSTIC
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay,
                      fontSize: 14, fontStyle: 'italic',
                      fontWeight: 300,
                      lineHeight: 1.55, color: T.ink,
                    }}
                  >
                    {stroke.diagnostic}
                  </div>
                </div>
              </div>
            </div>

            {/* Chapter navigation */}
            <div
              className="reveal reveal-4"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 0,
                paddingTop: 32,
                borderTop: `1px solid ${T.rule}`,
                marginBottom: 40,
              }}
            >
              {CHAPTERS.filter((c) => c.id !== chapterId).map((c) => {
                const currentIdx = CHAPTERS.findIndex((cc) => cc.id === chapterId);
                const cIdx = CHAPTERS.findIndex((cc) => cc.id === c.id);
                const kicker = cIdx === currentIdx + 1 ? 'NEXT CHAPTER'
                  : cIdx === currentIdx - 1 ? 'PREVIOUS CHAPTER'
                  : 'ALSO IN LIBRARY';
                return (
                <button
                  key={c.id}
                  onClick={() => setChapterId(c.id)}
                  className="ghost-btn"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textAlign: 'left',
                    color: T.ink,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 8,
                    }}
                  >
                    {kicker} &nbsp;·&nbsp; {c.number}
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontSize: 17,
                      lineHeight: 1.3, paddingRight: 16,
                    }}
                  >
                    {c.name} →
                  </div>
                </button>
                );
              })}
            </div>
          </>
        )}

        {/* Footer */}
        <footer
          style={{
            marginTop: 40,
            paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, letterSpacing: '0.1em',
          }}
        >
          <span>NO. 042 &nbsp;·&nbsp; TOOL 04 OF 09 &nbsp;·&nbsp; LIBRARY V1</span>
          <Link
            to="/tools/technique-library/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ANIMATED PANE — renders any stroke's path with appropriate tool glyph
// ═════════════════════════════════════════════════════════════════════════
function AnimatedPane({ stroke, current, visibleTrail, trailPoints, showGrid, t, T }) {
  const isCloth = current.tool === 'cloth';
  const isRazor = current.tool === 'razor';
  const isSleeve = current.tool === 'sleeve';
  const isSponge = current.tool === 'sponge';
  const isSpray = current.tool === 'spray';
  const isCursor = current.tool === 'cursor';
  const isPoleHead = current.tool === 'poleHead';
  const isApplication = isSleeve || isSponge || isSpray;

  // Application strokes deposit wetness instead of cleaning. The trail
  // mask is reused, but we paint a wet sheen ONTO the glass at those
  // positions instead of carving away dirt.

  // For the razor: distinguish "scratch" angles (>35°) from "scrape" angles
  const bladeAngle = current.bladeAngle || 0;
  const isUnsafeAngle = isRazor && bladeAngle > 35;

  return (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.rule}`,
        padding: 24,
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
      >
        <defs>
          <linearGradient id="glassGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={T.glass} stopOpacity="1" />
            <stop offset="50%" stopColor={T.glass} stopOpacity="0.85" />
            <stop offset="100%" stopColor={T.glass} stopOpacity="0.95" />
          </linearGradient>

          <pattern id="dirty" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="4" r="0.6" fill={T.inkFaint} opacity="0.4" />
            <circle cx="9" cy="11" r="0.4" fill={T.inkFaint} opacity="0.3" />
            <circle cx="11" cy="2" r="0.5" fill={T.inkFaint} opacity="0.35" />
            <circle cx="2" cy="10" r="0.3" fill={T.inkFaint} opacity="0.25" />
          </pattern>

          {/* Water bead pattern — used for the detail stroke perimeter */}
          <radialGradient id="bead" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="white" stopOpacity="0.7" />
            <stop offset="60%" stopColor={T.glassEdge} stopOpacity="0.4" />
            <stop offset="100%" stopColor={T.glassEdge} stopOpacity="0" />
          </radialGradient>

          {/* Wet sheen gradient — for application strokes */}
          <linearGradient id="wetSheen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={T.sage} stopOpacity="0.0" />
            <stop offset="40%" stopColor={T.sage} stopOpacity="0.32" />
            <stop offset="100%" stopColor={T.sage} stopOpacity="0.45" />
          </linearGradient>

          <mask id="cleanedMask">
            <rect x="0" y="0" width={W} height={H} fill="white" />
            {visibleTrail.length > 1 && (
              <polyline
                points={visibleTrail.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke="black"
                strokeWidth={isCloth ? 18 : isRazor ? 14 : 32}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </mask>

          {/* Wet mask — INVERSE of cleaned mask. Only paints sheen WHERE the
              application tool has visited (and was depositing). */}
          <mask id="wetMask">
            <rect x="0" y="0" width={W} height={H} fill="black" />
            {isApplication && (() => {
              // Only include trail segments where depositing was true
              const segs = [];
              let buf = [];
              trailPoints.forEach((p) => {
                if (p.t > t) return;
                if (p.depositing && !p.lifted) {
                  buf.push(p);
                } else if (buf.length > 1) {
                  segs.push(buf);
                  buf = [];
                } else {
                  buf = [];
                }
              });
              if (buf.length > 1) segs.push(buf);
              return segs.map((seg, idx) => (
                <polyline
                  key={idx}
                  points={seg.map((p) => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke="white"
                  strokeWidth={isSleeve ? 56 : isSponge ? 38 : 30}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.9"
                />
              ));
            })()}
          </mask>
        </defs>

        {/* Window frame */}
        <rect x="4" y="4" width={W - 8} height={H - 8} fill="none" stroke={T.glassEdge} strokeWidth="3" />

        {/* Clean glass */}
        <rect x="8" y="8" width={W - 16} height={H - 16} fill="url(#glassGrad)" />

        {/* Reflection grid */}
        {showGrid && (
          <g opacity="0.4">
            {[1, 2, 3].map((i) => (
              <line key={`v${i}`} x1={(W / 4) * i} y1="8" x2={(W / 4) * i} y2={H - 8} stroke={T.glassEdge} strokeWidth="0.5" strokeDasharray="2 6" />
            ))}
            {[1, 2].map((i) => (
              <line key={`h${i}`} x1="8" y1={(H / 3) * i} x2={W - 8} y2={(H / 3) * i} stroke={T.glassEdge} strokeWidth="0.5" strokeDasharray="2 6" />
            ))}
          </g>
        )}

        {/* Water beads on perimeter — only for L-perimeter stroke */}
        {stroke.id === 'lperim' && (
          <g>
            {Array.from({ length: 24 }, (_, i) => {
              // Distribute beads around the perimeter
              const inset = 8;
              const left = M + inset, right = W - M - inset;
              const top = M + inset, bot = H - M - inset;
              const perimLen = 2 * (right - left) + 2 * (bot - top);
              const pos = (i / 24) * perimLen;
              let bx, by;
              if (pos < right - left) { bx = left + pos; by = top; }
              else if (pos < (right - left) + (bot - top)) { bx = right; by = top + (pos - (right - left)); }
              else if (pos < 2 * (right - left) + (bot - top)) { bx = right - (pos - ((right - left) + (bot - top))); by = bot; }
              else { bx = left; by = bot - (pos - (2 * (right - left) + (bot - top))); }

              // Has the cloth passed this position yet?
              const beadT = pos / perimLen;
              const absorbed = beadT < t;
              return (
                <circle
                  key={i}
                  cx={bx} cy={by}
                  r={absorbed ? 0 : 3.5}
                  fill="url(#bead)"
                  style={{
                    transition: 'r 0.4s ease-out',
                  }}
                />
              );
            })}
          </g>
        )}

        {/* Dirt layer — masked */}
        <rect x="8" y="8" width={W - 16} height={H - 16} fill={T.dirty} mask="url(#cleanedMask)" />
        <rect x="8" y="8" width={W - 16} height={H - 16} fill="url(#dirty)" mask="url(#cleanedMask)" />

        {/* Wet sheen — only for application strokes */}
        {isApplication && (
          <>
            <rect
              x="8" y="8"
              width={W - 16} height={H - 16}
              fill="url(#wetSheen)"
              mask="url(#wetMask)"
              opacity="0.9"
            />
            {/* Subtle highlight streaks suggesting wet glass */}
            <rect
              x="8" y="8"
              width={W - 16} height={H - 16}
              fill={T.sage}
              opacity="0.18"
              mask="url(#wetMask)"
            />
          </>
        )}
        {/* Faint full-path preview */}
        <polyline
          points={trailPoints.filter((p) => !p.lifted).map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={T.trail}
          strokeWidth="0.8"
          strokeDasharray="3 5"
          opacity="0.3"
        />

        {/* Active trail */}
        {visibleTrail.length > 1 && (
          <polyline
            points={visibleTrail.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke={T.sienna}
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.8"
          />
        )}

        {/* Deposits (Edge Approach only) — visible spots on the glass */}
        {stroke.id === 'edgeApproach' && (
          <g>
            {[
              { x: W / 2 - 60, y: H * 0.35, active: 0 },
              { x: W / 2 + 60, y: H * 0.62, active: 1 },
            ].map((dep, idx) => {
              // Show deposit until cycle has scraped it
              const cyclePhase = Math.floor(t * 2);
              const cycleLocal = (t * 2) - cyclePhase;
              const isCurrentDeposit = cyclePhase === idx;
              const scrapedAway = cyclePhase > idx ||
                (isCurrentDeposit && cycleLocal > 0.65);
              const shrinking = isCurrentDeposit && cycleLocal > 0.4 && cycleLocal < 0.65;
              const r = scrapedAway ? 0 : shrinking ? 6 - (cycleLocal - 0.4) * 24 : 6;
              return (
                <g key={idx}>
                  <ellipse
                    cx={dep.x} cy={dep.y}
                    rx={Math.max(r, 0)} ry={Math.max(r * 0.7, 0)}
                    fill={T.ink}
                    opacity="0.55"
                    style={{ transition: 'rx 0.3s, ry 0.3s' }}
                  />
                  <ellipse
                    cx={dep.x - 1} cy={dep.y - 1}
                    rx={Math.max(r * 0.5, 0)} ry={Math.max(r * 0.35, 0)}
                    fill={T.ink}
                    opacity="0.7"
                    style={{ transition: 'rx 0.3s, ry 0.3s' }}
                  />
                </g>
              );
            })}
          </g>
        )}

        {/* Bucket visual — Sleeve Wash only, visible during load phase */}
        {stroke.id === 'sleeveWash' && t < 0.12 && (
          <g transform={`translate(${M + 60}, ${H + 30})`}>
            {/* Bucket body */}
            <path
              d="M -28 -8 L 28 -8 L 24 28 L -24 28 Z"
              fill={T.surface}
              stroke={T.ink}
              strokeWidth="2"
              opacity="0.95"
            />
            {/* Solution surface (oval cap) */}
            <ellipse cx="0" cy="-8" rx="28" ry="4" fill={T.sage} opacity="0.6" />
            <ellipse cx="0" cy="-7" rx="26" ry="3" fill={T.sage} opacity="0.4" />
            {/* Bucket rim */}
            <ellipse cx="0" cy="-8" rx="28" ry="4" fill="none" stroke={T.ink} strokeWidth="1.2" />
            {/* Handle */}
            <path
              d="M -28 -10 Q -38 -22, -34 -32 M 28 -10 Q 38 -22, 34 -32"
              fill="none"
              stroke={T.ink}
              strokeWidth="1.5"
            />
            <line x1="-34" y1="-32" x2="34" y2="-32" stroke={T.ink} strokeWidth="2" />
            {/* Label */}
            <text
              x="0" y="14"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="7"
              letterSpacing="1.3"
              fill={T.inkFaint}
              textAnchor="middle"
            >
              SOLUTION
            </text>
          </g>
        )}

        {/* Spray droplet burst — when spray is firing */}
        {stroke.id === 'spraySpread' && current.spraying && current.sprayBurstT !== undefined && (
          <g transform={`translate(${current.x}, ${current.y})`} opacity={1 - current.sprayBurstT * 0.7}>
            {/* Burst pattern: 9 droplets radiating outward */}
            {Array.from({ length: 9 }, (_, i) => {
              const angle = (i / 9) * Math.PI * 2 - Math.PI / 2;
              const burst = current.sprayBurstT;
              const dist = burst * 22;
              const dx = Math.cos(angle) * dist;
              const dy = Math.sin(angle) * dist;
              const r = (1 - burst) * 1.8 + 0.6;
              return (
                <circle
                  key={i}
                  cx={dx}
                  cy={dy}
                  r={r}
                  fill={T.sage}
                  opacity={0.7}
                />
              );
            })}
            {/* Center burst */}
            <circle cx="0" cy="0" r={4 - current.sprayBurstT * 3} fill={T.sage} opacity="0.5" />
          </g>
        )}

        {/* Persistent spray spot marks (Spray & Spread) — show where mists landed */}
        {stroke.id === 'spraySpread' && t >= 0.4 && (
          <g>
            {[
              { x: M + 50, y: M + 60 },
              { x: W - M - 50, y: M + 60 },
              { x: W / 2, y: H / 2 },
              { x: M + 50, y: H - M - 60 },
              { x: W - M - 50, y: H - M - 60 },
            ].map((spot, i) => {
              // Fade as the cloth spreads them
              const spreadT = (t - 0.4) / 0.6;
              const opacity = Math.max(0, 0.4 - spreadT * 0.5);
              return (
                <g key={i} opacity={opacity} style={{ transition: 'opacity 0.4s' }}>
                  <circle cx={spot.x} cy={spot.y} r="14" fill={T.sage} opacity="0.4" />
                  <circle cx={spot.x} cy={spot.y} r="8" fill={T.sage} opacity="0.5" />
                </g>
              );
            })}
          </g>
        )}

        {/* Channel-Wipe scene targets — cloth held in off-hand */}
        {stroke.id === 'crossBody' && (
          <g transform={`translate(${M - 10}, ${H - M + 8})`}>
            {/* Hand silhouette — abstracted as a small rectangle */}
            <rect
              x="-14" y="14"
              width="28" height="22"
              fill={T.ink}
              opacity="0.6"
              rx="3"
            />
            {/* Folded cloth — quartered square */}
            <rect
              x="-22" y="-18"
              width="44" height="34"
              fill={T.cloth}
              stroke={T.ink}
              strokeWidth="0.8"
              opacity="0.92"
              rx="2"
            />
            <line x1="-22" y1="-1" x2="22" y2="-1" stroke={T.ink} strokeWidth="0.5" opacity="0.3" />
            <line x1="0" y1="-18" x2="0" y2="16" stroke={T.ink} strokeWidth="0.5" opacity="0.3" />
            {/* Soiling marks accumulating with each pass */}
            {(() => {
              // Visible soil increases as each cycle's wipe completes
              const cyclesDone = Math.floor(t * 3);
              return (
                <g>
                  {cyclesDone >= 1 && (
                    <circle cx="-8" cy="-6" r="2.5" fill={T.ink} opacity="0.35" />
                  )}
                  {cyclesDone >= 2 && (
                    <>
                      <circle cx="6" cy="2" r="2" fill={T.ink} opacity="0.32" />
                      <circle cx="-4" cy="8" r="1.6" fill={T.ink} opacity="0.28" />
                    </>
                  )}
                  {cyclesDone >= 3 && (
                    <>
                      <circle cx="10" cy="-8" r="2.2" fill={T.ink} opacity="0.4" />
                      <circle cx="-12" cy="4" r="1.8" fill={T.ink} opacity="0.32" />
                    </>
                  )}
                </g>
              );
            })()}
            {/* Label */}
            <text
              x="0" y="48"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="7"
              letterSpacing="1.3"
              fill={T.inkFaint}
              textAnchor="middle"
            >
              CLOTH · OFF HAND
            </text>
          </g>
        )}

        {/* Channel-Wipe scene — cloth tucked at hip (Thigh Wipe) */}
        {stroke.id === 'thigh' && (
          <g transform={`translate(${(W - 2 * M) / 2 + M}, ${H - M + 8})`}>
            {/* Apron pocket band */}
            <rect
              x="-44" y="-2"
              width="88" height="6"
              fill={T.ink}
              opacity="0.45"
              rx="1"
            />
            {/* Hanging cloth — long rectangle */}
            <rect
              x="-12" y="2"
              width="24" height="36"
              fill={T.cloth}
              stroke={T.ink}
              strokeWidth="0.8"
              opacity="0.92"
              rx="1"
            />
            {/* Cloth fold ripples */}
            <line x1="-10" y1="14" x2="10" y2="14" stroke={T.ink} strokeWidth="0.4" opacity="0.25" />
            <line x1="-10" y1="22" x2="10" y2="22" stroke={T.ink} strokeWidth="0.4" opacity="0.25" />
            <line x1="-10" y1="30" x2="10" y2="30" stroke={T.ink} strokeWidth="0.4" opacity="0.25" />
            {/* Soiling */}
            {(() => {
              const cyclesDone = Math.floor(t * 5);
              return (
                <g>
                  {cyclesDone >= 1 && <circle cx="-2" cy="8" r="1.8" fill={T.ink} opacity="0.32" />}
                  {cyclesDone >= 2 && <circle cx="4" cy="14" r="1.6" fill={T.ink} opacity="0.32" />}
                  {cyclesDone >= 3 && <circle cx="-4" cy="20" r="1.5" fill={T.ink} opacity="0.3" />}
                  {cyclesDone >= 4 && <circle cx="2" cy="26" r="1.4" fill={T.ink} opacity="0.3" />}
                  {cyclesDone >= 5 && <circle cx="-2" cy="32" r="1.6" fill={T.ink} opacity="0.32" />}
                </g>
              );
            })()}
            {/* Label */}
            <text
              x="0" y="48"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="7"
              letterSpacing="1.3"
              fill={T.inkFaint}
              textAnchor="middle"
            >
              CLOTH · APRON
            </text>
          </g>
        )}

        {/* Channel-Wipe scene — bucket rim (Bucket-Edge Wipe) */}
        {stroke.id === 'bucketRim' && (
          <g transform={`translate(${(W - 2 * M) / 2 + M - 60}, ${H - M + 14})`}>
            {/* Bucket rim — curved ellipse showing oval cross-section */}
            <ellipse
              cx="60" cy="6"
              rx="64" ry="6"
              fill={T.surface}
              stroke={T.ink}
              strokeWidth="1.5"
              opacity="0.95"
            />
            {/* Solution surface inside rim */}
            <ellipse cx="60" cy="6" rx="60" ry="4" fill={T.sage} opacity="0.55" />
            {/* Front of bucket peeking down */}
            <path
              d="M -4 6 L 0 28 L 124 28 L 128 6"
              fill={T.surface}
              stroke={T.ink}
              strokeWidth="1.5"
              opacity="0.9"
            />
            {/* Soap scum on rim — accumulates */}
            {(() => {
              const cyclesDone = Math.floor(t * 2);
              return (
                <g>
                  {cyclesDone >= 1 && (
                    <ellipse cx="40" cy="3" rx="12" ry="1.5" fill={T.ink} opacity="0.3" />
                  )}
                  {cyclesDone >= 2 && (
                    <ellipse cx="80" cy="3" rx="14" ry="1.5" fill={T.ink} opacity="0.3" />
                  )}
                </g>
              );
            })()}
            {/* Label */}
            <text
              x="60" y="44"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="7"
              letterSpacing="1.3"
              fill={T.inkFaint}
              textAnchor="middle"
            >
              BUCKET RIM
            </text>
          </g>
        )}

        {/* Wipe flash — sienna burst when blade contacts cloth/rim */}
        {(stroke.id === 'crossBody' || stroke.id === 'thigh' || stroke.id === 'bucketRim') &&
          current.wiping && !current.lifted && (
          <g transform={`translate(${current.x}, ${current.y})`} opacity="0.7">
            <circle
              r="12"
              fill="none"
              stroke={T.sienna}
              strokeWidth="0.8"
              opacity="0.5"
              style={{ animation: 'pulse 0.8s ease-in-out infinite' }}
            />
            <circle
              r="20"
              fill="none"
              stroke={T.sienna}
              strokeWidth="0.5"
              opacity="0.3"
              style={{ animation: 'pulse 0.8s ease-in-out infinite' }}
            />
          </g>
        )}

        {/* Bucket Setup — Two-Bucket System: wash + rinse side by side */}
        {stroke.id === 'twoBucket' && (
          <g>
            {/* WASH bucket (left) */}
            <g transform={`translate(${M + 70}, ${H - M + 4})`}>
              <ellipse cx="0" cy="0" rx="36" ry="6" fill={T.surface} stroke={T.ink} strokeWidth="1.5" />
              <ellipse cx="0" cy="0" rx="34" ry="4.5" fill={T.sage} opacity="0.6" />
              <path
                d="M -36 0 L -32 38 L 32 38 L 36 0"
                fill={T.surface}
                stroke={T.ink}
                strokeWidth="1.5"
              />
              {/* Soap suds line */}
              <path
                d="M -32 -1 Q -16 -3, 0 -2 Q 16 -1, 32 -2"
                fill="none"
                stroke="white"
                strokeWidth="0.6"
                opacity="0.6"
              />
              <text
                x="0" y="54"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="8"
                letterSpacing="1.6"
                fill={current.atBucket === 'wash' ? T.sienna : T.inkFaint}
                textAnchor="middle"
                style={{ transition: 'fill 0.3s' }}
              >
                WASH
              </text>
            </g>
            {/* RINSE bucket (right) */}
            <g transform={`translate(${W - M - 70}, ${H - M + 4})`}>
              <ellipse cx="0" cy="0" rx="36" ry="6" fill={T.surface} stroke={T.ink} strokeWidth="1.5" />
              <ellipse cx="0" cy="0" rx="34" ry="4.5" fill={T.glassEdge} opacity="0.55" />
              <path
                d="M -36 0 L -32 38 L 32 38 L 36 0"
                fill={T.surface}
                stroke={T.ink}
                strokeWidth="1.5"
              />
              {/* Cloudier as cycles progress */}
              {(() => {
                const cyclesDone = Math.floor(t * 3);
                return (
                  <>
                    {cyclesDone >= 1 && (
                      <ellipse cx="-8" cy="0" rx="4" ry="1" fill={T.ink} opacity="0.18" />
                    )}
                    {cyclesDone >= 2 && (
                      <>
                        <ellipse cx="6" cy="-1" rx="5" ry="1" fill={T.ink} opacity="0.2" />
                        <ellipse cx="-14" cy="1" rx="3" ry="0.8" fill={T.ink} opacity="0.18" />
                      </>
                    )}
                    {cyclesDone >= 3 && (
                      <ellipse cx="0" cy="0" rx="20" ry="2" fill={T.ink} opacity="0.25" />
                    )}
                  </>
                );
              })()}
              <text
                x="0" y="54"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="8"
                letterSpacing="1.6"
                fill={current.atBucket === 'rinse' ? T.sienna : T.inkFaint}
                textAnchor="middle"
                style={{ transition: 'fill 0.3s' }}
              >
                RINSE
              </text>
            </g>
            {/* Connecting arrow showing flow direction */}
            <g opacity="0.35">
              <line
                x1={M + 110} y1={H - M + 14}
                x2={W - M - 110} y2={H - M + 14}
                stroke={T.inkFaint}
                strokeWidth="0.5"
                strokeDasharray="2 4"
              />
            </g>
          </g>
        )}

        {/* Bucket Setup — Sleeve Loading: single centered bucket */}
        {stroke.id === 'sleeveLoad' && (
          <g transform={`translate(${W / 2}, ${H - M + 4})`}>
            <ellipse cx="0" cy="0" rx="44" ry="8" fill={T.surface} stroke={T.ink} strokeWidth="1.5" />
            <ellipse cx="0" cy="0" rx="42" ry="6" fill={T.sage} opacity="0.6" />
            <path
              d="M -44 0 L -38 46 L 38 46 L 44 0"
              fill={T.surface}
              stroke={T.ink}
              strokeWidth="1.5"
            />
            {/* Bucket handle */}
            <path
              d="M -44 -2 Q -54 -16, -48 -28 M 44 -2 Q 54 -16, 48 -28"
              fill="none"
              stroke={T.ink}
              strokeWidth="1.5"
            />
            <line x1="-48" y1="-28" x2="48" y2="-28" stroke={T.ink} strokeWidth="2" />
            {/* Phase ripple — appears in solution surface during twist */}
            {current.phase === 'twist' && (
              <g style={{ animation: 'pulse 0.5s ease-in-out infinite' }}>
                <ellipse cx="0" cy="0" rx="38" ry="5" fill="none" stroke={T.sienna} strokeWidth="0.5" opacity="0.6" />
                <ellipse cx="0" cy="0" rx="30" ry="4" fill="none" stroke={T.sienna} strokeWidth="0.4" opacity="0.4" />
              </g>
            )}
            {/* Phase indicator — squeeze drips */}
            {current.phase === 'squeeze' && (
              <g>
                <ellipse cx="-20" cy="-3" rx="2" ry="3.5" fill={T.sage} opacity="0.7" />
                <ellipse cx="-4" cy="-3" rx="2" ry="3.5" fill={T.sage} opacity="0.7" />
                <ellipse cx="14" cy="-3" rx="2" ry="3.5" fill={T.sage} opacity="0.7" />
              </g>
            )}
            <text
              x="0" y="62"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="8"
              letterSpacing="1.6"
              fill={T.inkFaint}
              textAnchor="middle"
            >
              SOLUTION BUCKET
            </text>
          </g>
        )}

        {/* Bucket Setup — Tool Layout: items appear one by one */}
        {stroke.id === 'toolLayout' && (() => {
          const items = 6;
          const itemTimings = Array.from({ length: items }, (_, i) => i / items);
          // Each item is "placed" once t passes (i + 0.6)/items
          const isPlaced = (i) => t > (i + 0.6) / items;

          return (
            <g>
              {/* WASH bucket */}
              {isPlaced(0) && (
                <g transform={`translate(${M + 80}, ${H - M + 4})`}
                   style={{ animation: 'fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both' }}>
                  <ellipse cx="0" cy="0" rx="30" ry="5" fill={T.surface} stroke={T.ink} strokeWidth="1.2" />
                  <ellipse cx="0" cy="0" rx="28" ry="3.5" fill={T.sage} opacity="0.6" />
                  <path d="M -30 0 L -26 32 L 26 32 L 30 0" fill={T.surface} stroke={T.ink} strokeWidth="1.2" />
                  <text x="0" y="44" fontFamily="'JetBrains Mono', monospace" fontSize="7" letterSpacing="1.5" fill={T.inkFaint} textAnchor="middle">WASH</text>
                </g>
              )}
              {/* RINSE bucket */}
              {isPlaced(1) && (
                <g transform={`translate(${W - M - 80}, ${H - M + 4})`}
                   style={{ animation: 'fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both' }}>
                  <ellipse cx="0" cy="0" rx="30" ry="5" fill={T.surface} stroke={T.ink} strokeWidth="1.2" />
                  <ellipse cx="0" cy="0" rx="28" ry="3.5" fill={T.glassEdge} opacity="0.55" />
                  <path d="M -30 0 L -26 32 L 26 32 L 30 0" fill={T.surface} stroke={T.ink} strokeWidth="1.2" />
                  <text x="0" y="44" fontFamily="'JetBrains Mono', monospace" fontSize="7" letterSpacing="1.5" fill={T.inkFaint} textAnchor="middle">RINSE</text>
                </g>
              )}
              {/* SQUEEGEE — leaning */}
              {isPlaced(2) && (
                <g transform={`translate(${M + 30}, ${H - M - 30}) rotate(-12)`}
                   style={{ animation: 'fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both' }}>
                  <rect x="-2" y="-30" width="4" height="60" fill={T.ink} rx="1" />
                  <rect x="-30" y="30" width="60" height="3" fill={T.ink} />
                  <rect x="-30" y="33" width="60" height="2" fill={T.sienna} />
                  <text x="0" y="50" fontFamily="'JetBrains Mono', monospace" fontSize="7" letterSpacing="1.5" fill={T.inkFaint} textAnchor="middle" transform="rotate(12)">SQUEEGEE</text>
                </g>
              )}
              {/* SCRAPER — leaning, smaller */}
              {isPlaced(3) && (
                <g transform={`translate(${M + 70}, ${H - M - 16}) rotate(-15)`}
                   style={{ animation: 'fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both' }}>
                  <rect x="-1.5" y="-22" width="3" height="40" fill={T.ink} rx="1" />
                  <line x1="-8" y1="18" x2="8" y2="18" stroke={T.ink} strokeWidth="2.5" />
                  <text x="0" y="36" fontFamily="'JetBrains Mono', monospace" fontSize="7" letterSpacing="1.5" fill={T.inkFaint} textAnchor="middle" transform="rotate(15)">SCRAPER</text>
                </g>
              )}
              {/* CLOTHS — stacked */}
              {isPlaced(4) && (
                <g transform={`translate(${W - M - 36}, ${H - M - 30})`}
                   style={{ animation: 'fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both' }}>
                  <rect x="-16" y="0" width="32" height="6" fill={T.cloth} stroke={T.ink} strokeWidth="0.6" />
                  <rect x="-14" y="-6" width="28" height="6" fill={T.clothAlt} stroke={T.ink} strokeWidth="0.6" />
                  <rect x="-15" y="-12" width="30" height="6" fill={T.cloth} stroke={T.ink} strokeWidth="0.6" />
                  <text x="0" y="22" fontFamily="'JetBrains Mono', monospace" fontSize="7" letterSpacing="1.5" fill={T.inkFaint} textAnchor="middle">CLOTHS</text>
                </g>
              )}
              {/* APRON — abstract trapezoid */}
              {isPlaced(5) && (
                <g transform={`translate(${W / 2}, ${H - M - 28})`}
                   style={{ animation: 'fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both' }}>
                  <path d="M -22 -8 L -26 22 L 26 22 L 22 -8 Z" fill={T.ink} opacity="0.7" />
                  <line x1="-22" y1="-8" x2="22" y2="-8" stroke={T.sienna} strokeWidth="1.5" />
                  <text x="0" y="38" fontFamily="'JetBrains Mono', monospace" fontSize="7" letterSpacing="1.5" fill={T.inkFaint} textAnchor="middle">APRON</text>
                </g>
              )}

              {/* "Adding..." label callout near current item being dropped */}
              {current.itemDropping && current.itemLabel && (
                <g transform={`translate(${current.x + 24}, ${current.y - 8})`}>
                  <text
                    fontFamily="'JetBrains Mono', monospace"
                    fontSize="9"
                    letterSpacing="1.6"
                    fill={T.sienna}
                    style={{ animation: 'pulse 0.8s ease-in-out infinite' }}
                  >
                    + {current.itemLabel}
                  </text>
                </g>
              )}
            </g>
          );
        })()}

        {/* Tilt-In Reach — the sash itself, latches, body silhouette */}
        {(stroke.id === 'sashTilt' || stroke.id === 'reachAcross' || stroke.id === 'reSeat') && (() => {
          const sashAngle = current.sashAngle ?? (stroke.id === 'reachAcross' ? 45 : 0);
          // Pivot point is at the bottom of the sash
          const pivotY = H - M - 60;
          const sashHeight = (H - M - 60) - (M + 8);
          // As sashAngle grows, the top of the sash moves down + forward (we
          // simulate forward by reducing the apparent height — like a top-down
          // foreshortening). Use cosine for height, sine for downward shift.
          const angleRad = (sashAngle * Math.PI) / 180;
          const apparentHeight = sashHeight * Math.cos(angleRad);
          const topY = pivotY - apparentHeight;
          const sashLeft = M + 16;
          const sashRight = W - M - 16;
          const sashWidth = sashRight - sashLeft;

          // Outer pane visibility — appears behind sash as it tilts
          const outerVisible = sashAngle > 5;

          return (
            <g>
              {/* Outer pane revealed (rendered behind sash, only visible when tilted) */}
              {outerVisible && (
                <g opacity={Math.min(sashAngle / 30, 1)}>
                  {/* Diagonal hatching to suggest "outdoor" / outer pane */}
                  <rect
                    x={sashLeft + 4}
                    y={M + 8}
                    width={sashWidth - 8}
                    height={pivotY - (M + 8) - 4}
                    fill={T.glassEdge}
                    opacity="0.3"
                  />
                  <rect
                    x={sashLeft + 4}
                    y={M + 8}
                    width={sashWidth - 8}
                    height={pivotY - (M + 8) - 4}
                    fill="none"
                    stroke={T.inkFaint}
                    strokeWidth="0.5"
                    strokeDasharray="3 6"
                  />
                  {/* "OUTSIDE" label */}
                  <text
                    x={W / 2} y={M + 28}
                    fontFamily="'JetBrains Mono', monospace"
                    fontSize="9"
                    letterSpacing="1.6"
                    fill={T.inkFaint}
                    textAnchor="middle"
                  >
                    OUTSIDE PANE
                  </text>
                </g>
              )}

              {/* The sash — frame + glass, foreshortened by sashAngle */}
              <g>
                {/* Top rail (which moves) */}
                <rect
                  x={sashLeft}
                  y={topY - 4}
                  width={sashWidth}
                  height={8}
                  fill={T.ink}
                  opacity="0.85"
                  style={{ transition: 'y 0.05s linear' }}
                />
                {/* Side rails — drawn as diagonals when sash is tilted */}
                <line
                  x1={sashLeft + 2} y1={topY}
                  x2={sashLeft + 2} y2={pivotY}
                  stroke={T.ink}
                  strokeWidth="6"
                  opacity="0.85"
                />
                <line
                  x1={sashRight - 2} y1={topY}
                  x2={sashRight - 2} y2={pivotY}
                  stroke={T.ink}
                  strokeWidth="6"
                  opacity="0.85"
                />
                {/* Bottom rail */}
                <rect
                  x={sashLeft}
                  y={pivotY - 4}
                  width={sashWidth}
                  height={8}
                  fill={T.ink}
                  opacity="0.85"
                />
                {/* Glass within sash — gets a tilt-in shadow as it moves */}
                <rect
                  x={sashLeft + 6}
                  y={topY + 4}
                  width={sashWidth - 12}
                  height={pivotY - topY - 8}
                  fill="url(#glassGrad)"
                  opacity={0.85 - sashAngle * 0.005}
                />
                {/* Tilt shadow at base (suggests sash leaning forward) */}
                {sashAngle > 5 && (
                  <ellipse
                    cx={W / 2} cy={pivotY + 6}
                    rx={sashWidth / 2}
                    ry={sashAngle * 0.3}
                    fill={T.ink}
                    opacity="0.18"
                  />
                )}
              </g>

              {/* Latches at top corners */}
              {[
                { x: sashLeft + 32, side: 'left', released: current.latchLeftReleased || stroke.id === 'reachAcross', locked: current.latchLeftLocked },
                { x: sashRight - 32, side: 'right', released: current.latchRightReleased || stroke.id === 'reachAcross', locked: current.latchRightLocked },
              ].map((latch) => {
                const isActive = current.atLatch && current.latchSide === latch.side;
                const released = latch.released && !latch.locked;
                return (
                  <g key={latch.side} transform={`translate(${latch.x}, ${topY - 4})`}>
                    {/* Latch body */}
                    <rect
                      x="-7" y="-4"
                      width="14" height="8"
                      fill={released ? T.sienna : T.ink}
                      opacity="0.9"
                      stroke={T.ink}
                      strokeWidth="0.5"
                      style={{ transition: 'fill 0.3s' }}
                    />
                    {/* Latch arrow — points sideways when locked, down/out when released */}
                    <path
                      d={released ? 'M -3 -1 L 3 -1 L 0 4 Z' : 'M -1 -3 L -1 3 L 4 0 Z'}
                      fill={released ? '#fff' : T.bg}
                      opacity="0.9"
                    />
                    {/* Active flash */}
                    {isActive && (
                      <circle
                        r="14"
                        fill="none"
                        stroke={T.sienna}
                        strokeWidth="1"
                        opacity="0.6"
                        style={{ animation: 'pulse 0.6s ease-in-out infinite' }}
                      />
                    )}
                  </g>
                );
              })}

              {/* Reach envelope visualization (Reach-Across only) */}
              {stroke.id === 'reachAcross' && (
                <g>
                  {/* Safe reach zone — dashed semicircle from a fixed body position */}
                  <path
                    d={`M ${M + 20} ${H - M - 60} A 240 240 0 0 1 ${W - M - 20} ${H - M - 60}`}
                    fill="none"
                    stroke={T.sage}
                    strokeWidth="0.6"
                    strokeDasharray="4 6"
                    opacity="0.45"
                  />
                  {/* Body silhouette — minimal abstraction */}
                  <g transform={`translate(${W / 2}, ${H - M - 40})`}>
                    <ellipse cx="0" cy="0" rx="14" ry="6" fill={T.ink} opacity="0.45" />
                    <text
                      x="0" y="20"
                      fontFamily="'JetBrains Mono', monospace"
                      fontSize="7"
                      letterSpacing="1.5"
                      fill={T.inkFaint}
                      textAnchor="middle"
                    >
                      STANCE
                    </text>
                  </g>
                  {/* Reach extent indicator */}
                  {current.reachExtent !== undefined && (
                    <g transform={`translate(${W - M - 80}, ${M + 50})`}>
                      <text
                        fontFamily="'JetBrains Mono', monospace"
                        fontSize="9"
                        letterSpacing="1.4"
                        fill={current.reachExtent > 0.7 ? T.sienna : T.sage}
                        textAnchor="end"
                        style={{ transition: 'fill 0.3s' }}
                      >
                        REACH {Math.round(current.reachExtent * 100)}%
                      </text>
                    </g>
                  )}
                </g>
              )}
            </g>
          );
        })()}

        {/* Pole Technique — high pane, ground line, extension pole, water effects */}
        {(stroke.id === 'lowAnglePole' || stroke.id === 'twoStorySweep' || stroke.id === 'brushAndRinse') && (() => {
          // Ground anchor (where the cleaner stands) — bottom-center
          const groundY = H - M - 8;
          const groundX = W / 2;

          // Pane region (upper portion of canvas, indicating height)
          const paneTop = M + 4;
          const paneBot = stroke.id === 'lowAnglePole' ? M + 130
            : stroke.id === 'twoStorySweep' ? M + 140
            : M + 140;
          const paneLeft = stroke.id === 'lowAnglePole' ? M + 60
            : stroke.id === 'twoStorySweep' ? M + 50
            : M + 80;
          const paneRight = W - paneLeft - (W - 2 * M - paneLeft + M);
          // Recompute paneRight properly
          const paneRightActual = W - (paneLeft - M) - M;

          return (
            <g>
              {/* "Wall" extending below the pane to suggest building height */}
              <rect
                x={paneLeft - 6}
                y={paneBot + 2}
                width={paneRightActual - paneLeft + 12}
                height={groundY - paneBot - 30}
                fill={T.surface}
                stroke={T.rule}
                strokeWidth="0.5"
                opacity="0.5"
              />
              {/* Brick lines suggesting a building wall */}
              {[paneBot + 30, paneBot + 60, paneBot + 90, paneBot + 120].map((y) => (
                y < groundY - 30 && (
                  <line
                    key={y}
                    x1={paneLeft - 6}
                    y1={y}
                    x2={paneRightActual + 6}
                    y2={y}
                    stroke={T.rule}
                    strokeWidth="0.3"
                    opacity="0.4"
                    strokeDasharray="6 12"
                  />
                )
              ))}

              {/* Ground line */}
              <line
                x1={M}
                y1={groundY}
                x2={W - M}
                y2={groundY}
                stroke={T.ink}
                strokeWidth="1"
                opacity="0.5"
              />
              {/* Ground hatching */}
              {Array.from({ length: 12 }, (_, i) => {
                const gx = M + (i + 0.5) * ((W - 2 * M) / 12);
                return (
                  <line
                    key={i}
                    x1={gx} y1={groundY}
                    x2={gx - 4} y2={groundY + 6}
                    stroke={T.inkFaint}
                    strokeWidth="0.4"
                    opacity="0.5"
                  />
                );
              })}

              {/* "GROUND" label */}
              <text
                x={M + 4}
                y={groundY - 4}
                fontFamily="'JetBrains Mono', monospace"
                fontSize="8"
                letterSpacing="1.5"
                fill={T.inkFaint}
              >
                GROUND LEVEL
              </text>

              {/* Cleaner figure at ground — minimal body silhouette */}
              <g transform={`translate(${groundX}, ${groundY - 4})`}>
                {/* Body */}
                <rect x="-8" y="-30" width="16" height="30" fill={T.ink} opacity="0.6" rx="2" />
                {/* Head */}
                <circle cx="0" cy="-36" r="6" fill={T.ink} opacity="0.7" />
                {/* Apron */}
                <path d="M -10 -22 L -12 -2 L 12 -2 L 10 -22 Z" fill={T.sienna} opacity="0.5" />
              </g>

              {/* The pole — extends from cleaner's hands up to the brush head */}
              {(() => {
                // Pole base: cleaner's hands
                const baseX = groundX;
                const baseY = groundY - 26;
                // Pole head: current x/y position
                const headX = current.x;
                const headY = current.y;

                // Compute pole length for label
                const poleLen = Math.sqrt(
                  Math.pow(headX - baseX, 2) + Math.pow(headY - baseY, 2)
                );

                return (
                  <g>
                    {/* Pole shaft */}
                    <line
                      x1={baseX} y1={baseY}
                      x2={headX} y2={headY + 8}
                      stroke={T.ink}
                      strokeWidth="2.5"
                      opacity="0.85"
                    />
                    {/* Pole highlight (suggests cylindrical) */}
                    <line
                      x1={baseX - 1} y1={baseY}
                      x2={headX - 1} y2={headY + 8}
                      stroke={T.surface}
                      strokeWidth="0.6"
                      opacity="0.5"
                    />
                    {/* Pole length readout near middle */}
                    <text
                      x={(baseX + headX) / 2 + 12}
                      y={(baseY + headY) / 2}
                      fontFamily="'JetBrains Mono', monospace"
                      fontSize="9"
                      letterSpacing="1.2"
                      fill={T.inkFaint}
                      opacity="0.8"
                    >
                      ~{Math.round(poleLen / 12)} FT
                    </text>
                  </g>
                );
              })()}

              {/* Water jet droplets — when rinsing or jet-active */}
              {(current.rinsing || current.jetsActive) && !current.lifted && (
                <g transform={`translate(${current.x}, ${current.y + 8})`}>
                  {/* Falling water droplets */}
                  {Array.from({ length: 6 }, (_, i) => {
                    const dropX = (i - 2.5) * 4;
                    const dropY = 4 + (i % 3) * 6;
                    return (
                      <ellipse
                        key={i}
                        cx={dropX} cy={dropY}
                        rx="1" ry="2.5"
                        fill={T.sage}
                        opacity={0.7 - (i % 3) * 0.15}
                        style={{ animation: 'pulse 0.6s ease-in-out infinite' }}
                      />
                    );
                  })}
                </g>
              )}

              {/* Water sheeting — visible during gravity-dry phase */}
              {current.sheeting && (
                <g opacity={1 - current.sheetingT * 0.6}>
                  {/* Water sheets descending the pane */}
                  {[0.2, 0.4, 0.6, 0.8].map((xRatio, i) => (
                    <line
                      key={i}
                      x1={paneLeft + xRatio * (paneRightActual - paneLeft)}
                      y1={paneTop + 8}
                      x2={paneLeft + xRatio * (paneRightActual - paneLeft)}
                      y2={paneBot - 4}
                      stroke={T.sage}
                      strokeWidth="1"
                      opacity={0.5}
                      strokeDasharray="3 8"
                    />
                  ))}
                  {/* "SHEETING" label */}
                  <text
                    x={(paneLeft + paneRightActual) / 2}
                    y={paneTop + 30}
                    fontFamily="'JetBrains Mono', monospace"
                    fontSize="9"
                    letterSpacing="1.6"
                    fill={T.sage}
                    textAnchor="middle"
                  >
                    SHEETING ↓
                  </text>
                </g>
              )}

              {/* Wobble level callout (Low-Angle Pole only) */}
              {stroke.id === 'lowAnglePole' && current.wobbleLevel && (
                <g transform={`translate(${W - M - 4}, ${paneBot + 16})`}>
                  <text
                    fontFamily="'JetBrains Mono', monospace"
                    fontSize="9"
                    letterSpacing="1.4"
                    fill={
                      current.wobbleLevel === 'controlled' ? T.sage :
                      current.wobbleLevel === 'drifting' ? T.sienna :
                      T.sage
                    }
                    textAnchor="end"
                    style={{ transition: 'fill 0.3s' }}
                  >
                    {current.wobbleLevel === 'controlled' && '✓ CONTROLLED'}
                    {current.wobbleLevel === 'drifting' && '✕ DRIFTING'}
                    {current.wobbleLevel === 'compensated' && '✓ COMPENSATED'}
                  </text>
                </g>
              )}
            </g>
          );
        })()}

        {/* Tool glyph — squeegee, cloth, razor, sleeve, sponge, or spray */}
        <g
          transform={`translate(${current.x}, ${current.y}) rotate(${current.angle || 0}) ${current.scale ? `scale(${current.scale})` : ''}`}
          style={{
            opacity: current.lifted ? 0.4 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {isRazor && (
            <RazorGlyph
              T={T}
              bladeAngle={bladeAngle}
              isUnsafe={isUnsafeAngle}
              scraping={current.scraping}
              lifted={current.lifted}
            />
          )}
          {isSleeve && (
            <SleeveGlyph T={T} isLoading={current.isLoading} depositing={current.depositing} />
          )}
          {isSponge && (
            <SpongeGlyph T={T} pressing={current.pressing} />
          )}
          {isSpray && (
            <SprayGlyph T={T} spraying={current.spraying} />
          )}
          {isCloth && (
            <ClothGlyph
              T={T}
              faceIndex={current.faceIndex || 0}
              isPinch={current.isPinch}
              isLPerim={stroke.id === 'lperim'}
            />
          )}
          {isCursor && (
            // Item-drop cursor — small chevron + halo, used in Tool Layout
            <g>
              <circle r="14" fill="none" stroke={T.sienna} strokeWidth="0.8" opacity="0.4" style={{ animation: 'pulse 1.2s ease-in-out infinite' }} />
              <path d="M -6 -4 L 0 4 L 6 -4" fill="none" stroke={T.sienna} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          )}
          {isPoleHead && (
            <PoleHeadGlyph
              T={T}
              bristlesActive={current.bristlesActive || current.scrubbing}
              jetsActive={current.jetsActive || current.rinsing}
            />
          )}
          {!isRazor && !isSleeve && !isSponge && !isSpray && !isCloth && !isCursor && !isPoleHead && (
            // Squeegee (default)
            <>
              <rect x="-3" y="-44" width="6" height="36" fill={T.ink} rx="1.5" />
              <rect x="-22" y="-12" width="44" height="6" fill={T.ink} rx="1" />
              <rect x="-58" y="-6" width="116" height="5" fill={T.ink} />
              <rect x="-58" y="-1" width="116" height="3" fill={T.sienna} />
              <line x1="-58" y1="2" x2="58" y2="2" stroke={T.ink} strokeWidth="0.5" opacity="0.6" />
              {!current.lifted && (
                <circle cx="0" cy="-2" r="2" fill={T.sienna} style={{ animation: 'pulse 1.4s ease-in-out infinite' }} />
              )}
            </>
          )}
        </g>

        {/* Top-corner readouts */}
        <g transform={`translate(${M + 4}, ${M - 4})`}>
          <text fontFamily="'JetBrains Mono', monospace" fontSize="9" letterSpacing="1.4" fill={T.inkFaint}>
            {`${Math.round(t * 100).toString().padStart(2, '0')}%`}
          </text>
        </g>
        <g transform={`translate(${W - M - 4}, ${M - 4})`}>
          <text fontFamily="'JetBrains Mono', monospace" fontSize="9" letterSpacing="1.4" fill={T.inkFaint} textAnchor="end">
            {stroke.italic.toUpperCase()}
          </text>
        </g>

        {/* Phase indicator — Sleeve Wash */}
        {stroke.id === 'sleeveWash' && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              PHASE
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={T.sienna}
            >
              {current.phase === 'load' ? 'Load' :
               current.phase === 'edge' ? 'Edge-prime' :
               'Fill'}
              {current.phase === 'fill' && current.columnIndex !== undefined && (
                <tspan fontStyle="normal" fontFamily="'JetBrains Mono', monospace" fontSize="11" fill={T.inkFaint} letterSpacing="1.4">
                  {' '}· COLUMN {current.columnIndex + 1}/3
                </tspan>
              )}
            </text>
          </g>
        )}

        {/* Dab counter — Sea Sponge */}
        {stroke.id === 'spongeDab' && current.dabIndex !== undefined && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              DAB
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontSize="26"
              fill={T.ink}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {String(current.dabIndex + 1).padStart(2, '0')}
              <tspan fontFamily="'JetBrains Mono', monospace" fontSize="12" fill={T.inkFaint} letterSpacing="1.2">
                {' '}/ 20
              </tspan>
            </text>
            {current.pressing && (
              <text
                x="84" y="22"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="10"
                letterSpacing="1.6"
                fill={T.sienna}
                style={{ animation: 'pulse 0.8s ease-in-out infinite' }}
              >
                ◆ PRESS
              </text>
            )}
          </g>
        )}

        {/* Spray phase counter */}
        {stroke.id === 'spraySpread' && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              {t < 0.4 ? 'MIST' : 'SPREAD'}
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={T.sienna}
            >
              {t < 0.4 && current.sprayIndex !== undefined ?
                `Spot ${current.sprayIndex + 1}/5` :
                'Top-down'}
            </text>
          </g>
        )}

        {/* Channel-Wipe phase + pass counter */}
        {(stroke.id === 'crossBody' || stroke.id === 'thigh' || stroke.id === 'bucketRim') && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              PHASE &nbsp;·&nbsp; PASS {(current.pass || 0) + 1}/
              {stroke.id === 'crossBody' ? '3' : stroke.id === 'thigh' ? '5' : '2'}
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={T.sienna}
            >
              {current.phase === 'stroke' ? 'On glass' :
               current.phase === 'travel' ? 'In transit' :
               current.phase === 'wipe' ? 'Wiping blade' :
               current.phase === 'flip' ? 'Flipping' :
               current.phase === 'return' ? 'Returning' :
               'Ready'}
            </text>
          </g>
        )}

        {/* Bucket Setup readouts */}
        {stroke.id === 'twoBucket' && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              CYCLE {(current.pass || 0) + 1}/3
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={T.sienna}
            >
              {current.phase === 'wash-dip' || current.phase === 'wash-rise' ? 'Loading wash' :
               current.phase === 'travel-to-pane' ? 'To pane' :
               current.phase === 'apply' ? 'Applying' :
               current.phase === 'travel-to-rinse' ? 'To rinse' :
               current.phase === 'rinse-dip' || current.phase === 'rinse-rise' ? 'Rinsing' :
               'Returning'}
            </text>
          </g>
        )}
        {stroke.id === 'sleeveLoad' && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              STEP
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={T.sienna}
            >
              {current.phase === 'submerge' ? '1 — Submerge' :
               current.phase === 'twist' ? '2 — Twist' :
               current.phase === 'lift' ? '3 — Lift' :
               current.phase === 'squeeze' ? '4 — Squeeze' :
               'Ready'}
            </text>
          </g>
        )}
        {stroke.id === 'toolLayout' && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              ITEM {Math.min((current.itemIndex || 0) + 1, 6)}/6
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontSize="22"
              fill={T.ink}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {current.itemLabel ? current.itemLabel : 'Setting up'}
            </text>
          </g>
        )}

        {/* Tilt-In Reach phase + sash angle readout */}
        {(stroke.id === 'sashTilt' || stroke.id === 'reSeat') && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              SASH ANGLE
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontSize="26"
              fill={T.ink}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {Math.round(current.sashAngle || 0)}°
              <tspan
                fontFamily="'Fraunces', serif"
                fontStyle="italic"
                fontSize="18"
                fill={T.sienna}
                fontWeight="400"
              >
                {' '}·{' '}
                {current.phase === 'approach-left' ? 'Approach left latch' :
                 current.phase === 'release-left' ? 'Release left' :
                 current.phase === 'approach-right' ? 'Approach right latch' :
                 current.phase === 'release-right' ? 'Release right' :
                 current.phase === 'guide-tilt' ? 'Guide inward' :
                 current.phase === 'lift' ? 'Lift to vertical' :
                 current.phase === 'approach-left-lock' ? 'To left lock' :
                 current.phase === 'lock-left' ? 'Lock left' :
                 current.phase === 'approach-right-lock' ? 'To right lock' :
                 current.phase === 'lock-right' ? 'Lock right' : ''}
              </tspan>
            </text>
          </g>
        )}

        {/* Reach-Across stance readout */}
        {stroke.id === 'reachAcross' && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              STANCE
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={current.reachExtent > 0.7 ? T.sienna : T.sienna}
            >
              {current.reachExtent > 0.7 ? 'Forearm extending' : 'Both feet flat'}
            </text>
          </g>
        )}

        {/* Pole Technique readouts */}
        {(stroke.id === 'lowAnglePole' || stroke.id === 'twoStorySweep' || stroke.id === 'brushAndRinse') && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              MODE
              {(current.pass !== undefined) && (
                <tspan> &nbsp;·&nbsp; PASS {(current.pass || 0) + 1}</tspan>
              )}
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="22"
              fill={T.sienna}
            >
              {current.phase === 'short-pole' ? 'Short pole, controlled' :
               current.phase === 'long-pole' ? 'Long pole, drifting' :
               current.phase === 'corrected' ? 'Long pole, corrected' :
               current.phase === 'scrub' ? 'Scrubbing' :
               current.phase === 'rinse' ? 'Rinsing' :
               current.phase === 'gravity-dry' ? 'Drying — sheeting' :
               current.phase === 'finish' ? 'Final rinse' :
               'Working'}
            </text>
          </g>
        )}

        {/* Pinch indicator label — only for corner pinch */}
        {stroke.id === 'pinch' && current.isPinch && (
          <g transform={`translate(${current.x + 30}, ${current.y - 30})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.6"
              fill={T.sienna}
              style={{ animation: 'pulse 1s ease-in-out infinite' }}
            >
              ◆ PINCH
            </text>
          </g>
        )}

        {/* Razor: live angle readout + verdict (bottom-left of pane) */}
        {isRazor && (
          <g transform={`translate(${M + 4}, ${H - M - 8})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.4"
              fill={T.inkFaint}
            >
              BLADE ANGLE
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontSize="28"
              fontWeight="400"
              fill={isUnsafeAngle ? T.sienna : T.ink}
              style={{
                fontVariantNumeric: 'tabular-nums',
                transition: 'fill 0.3s',
              }}
            >
              {Math.round(bladeAngle)}°
            </text>
            <text
              x="48" y="22"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="10"
              letterSpacing="1.6"
              fill={isUnsafeAngle ? T.sienna : T.sage}
              style={{ transition: 'fill 0.3s' }}
            >
              {isUnsafeAngle ? '✕ SCRATCHES' : '✓ SCRAPES'}
            </text>
          </g>
        )}

        {/* Razor: angle test verdict label (top-center of pane) */}
        {stroke.id === 'angleTest' && (
          <g transform={`translate(${W / 2}, ${M + 40})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="9"
              letterSpacing="1.6"
              fill={T.inkFaint}
              textAnchor="middle"
            >
              TESTING {current.angleValue}° &nbsp;·&nbsp; {current.angleStage + 1} OF 4
            </text>
            <text
              x="0" y="22"
              fontFamily="'Fraunces', serif"
              fontStyle="italic"
              fontSize="18"
              fill={current.angleValue <= 35 ? T.ink : T.sienna}
              textAnchor="middle"
              style={{ transition: 'fill 0.4s' }}
            >
              {current.angleValue === 25 && 'Working angle.'}
              {current.angleValue === 45 && 'Risky on coatings.'}
              {current.angleValue === 60 && 'Now you are pressing.'}
              {current.angleValue === 80 && 'This is how scratches happen.'}
            </text>
          </g>
        )}

        {/* Razor: scratch warning marks at unsafe angles (visual penalty) */}
        {isRazor && isUnsafeAngle && !current.lifted && (
          <g style={{ animation: 'pulse 0.8s ease-in-out infinite' }}>
            <line
              x1={current.x - 30} y1={current.y + 6}
              x2={current.x + 30} y2={current.y + 6}
              stroke={T.sienna}
              strokeWidth="1"
              strokeDasharray="2 3"
              opacity="0.7"
            />
            <line
              x1={current.x - 24} y1={current.y + 9}
              x2={current.x + 24} y2={current.y + 9}
              stroke={T.sienna}
              strokeWidth="0.6"
              strokeDasharray="1 2"
              opacity="0.5"
            />
          </g>
        )}

        {/* Razor: scrape direction arrow under the blade */}
        {isRazor && current.scraping && !current.lifted && (
          <g transform={`translate(${current.x}, ${current.y + 24})`}>
            <text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="11"
              fill={T.sage}
              textAnchor="middle"
              opacity="0.7"
            >
              ←
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

// ─── Razor glyph: blade at variable angle, with handle ───────────────────
function RazorGlyph({ T, bladeAngle, isUnsafe, scraping, lifted }) {
  // bladeAngle = degrees off the glass surface (0° = flat, 90° = perpendicular)
  // The blade is rendered as a long thin strip rotated by -bladeAngle so the
  // tip touches the glass at origin (0,0) and the body angles up-right.

  const bladeLen = 38;
  const handleLen = 60;
  const angleRad = (bladeAngle * Math.PI) / 180;

  // Blade tip is at (0, 0). Blade body extends up and to the right at angle.
  // Working edge of blade hits glass; rest tilts upward.
  const tipX = 0, tipY = 0;
  const bladeBackX = -Math.cos(angleRad) * bladeLen;
  const bladeBackY = -Math.sin(angleRad) * bladeLen;

  // Handle continues from blade back, at the same angle but slightly steeper
  const handleAngleRad = ((bladeAngle + 12) * Math.PI) / 180;
  const handleEndX = bladeBackX - Math.cos(handleAngleRad) * handleLen;
  const handleEndY = bladeBackY - Math.sin(handleAngleRad) * handleLen;

  return (
    <g>
      {/* Wet film under the blade — only when scraping at safe angle */}
      {scraping && !isUnsafe && !lifted && (
        <ellipse
          cx="-10" cy="2"
          rx="22" ry="2.5"
          fill={T.sage}
          opacity="0.35"
        />
      )}

      {/* Blade (the dangerous part) */}
      <line
        x1={tipX} y1={tipY}
        x2={bladeBackX} y2={bladeBackY}
        stroke={isUnsafe ? T.sienna : T.ink}
        strokeWidth="3"
        strokeLinecap="square"
      />
      {/* Blade thickness — second line just above for the metal body */}
      <line
        x1={tipX - Math.sin(angleRad) * 1.8}
        y1={tipY + Math.cos(angleRad) * 1.8}
        x2={bladeBackX - Math.sin(angleRad) * 1.8}
        y2={bladeBackY + Math.cos(angleRad) * 1.8}
        stroke={isUnsafe ? T.sienna : T.ink}
        strokeWidth="2"
        opacity="0.7"
        strokeLinecap="square"
      />

      {/* Handle (holder for the blade) */}
      <line
        x1={bladeBackX} y1={bladeBackY}
        x2={handleEndX} y2={handleEndY}
        stroke={T.ink}
        strokeWidth="6"
        strokeLinecap="round"
      />
      {/* Handle grip detail */}
      <line
        x1={bladeBackX - Math.cos(handleAngleRad) * 18}
        y1={bladeBackY - Math.sin(handleAngleRad) * 18}
        x2={bladeBackX - Math.cos(handleAngleRad) * 42}
        y2={bladeBackY - Math.sin(handleAngleRad) * 42}
        stroke={T.sienna}
        strokeWidth="3"
        opacity="0.7"
      />

      {/* Glass surface line for reference (extends through the tip) */}
      {!lifted && (
        <line
          x1="-50" y1="0"
          x2="50" y2="0"
          stroke={T.glassEdge}
          strokeWidth="0.5"
          strokeDasharray="2 3"
          opacity="0.5"
        />
      )}

      {/* Angle arc — visual indicator of the angle between blade and glass */}
      {!lifted && (
        <path
          d={`M 14 0 A 14 14 0 0 0 ${14 * Math.cos(angleRad)} ${-14 * Math.sin(angleRad)}`}
          fill="none"
          stroke={isUnsafe ? T.sienna : T.sage}
          strokeWidth="1"
          opacity="0.7"
          style={{ transition: 'stroke 0.3s' }}
        />
      )}

      {/* Tip indicator */}
      {scraping && !lifted && (
        <circle
          cx={tipX} cy={tipY}
          r="2"
          fill={isUnsafe ? T.sienna : T.sage}
          style={{ animation: 'pulse 1.4s ease-in-out infinite' }}
        />
      )}
    </g>
  );
}

// ─── Cloth glyph: folded microfiber, rotates faces ───────────────────────
function ClothGlyph({ T, faceIndex, isPinch, isLPerim }) {
  // Cycle through face colors to show rotation between passes
  const faceColors = [T.cloth, T.clothAlt, T.cloth, T.clothAlt];
  const face = faceColors[faceIndex % faceColors.length];

  if (isPinch) {
    // Pinched corner shape — triangular
    return (
      <g style={{ animation: 'pinchPulse 1s ease-in-out infinite' }}>
        <path
          d="M -16 -16 L 16 -16 L 0 14 Z"
          fill={face}
          opacity="0.85"
          stroke={T.ink}
          strokeWidth="0.8"
        />
        {/* Fold line */}
        <line x1="-8" y1="-8" x2="0" y2="6" stroke={T.ink} strokeWidth="0.5" opacity="0.4" />
        <line x1="8" y1="-8" x2="0" y2="6" stroke={T.ink} strokeWidth="0.5" opacity="0.4" />
      </g>
    );
  }

  // Standard folded square — quartered for L-perimeter, full for lap-lift
  return (
    <g>
      <rect
        x="-22" y="-22"
        width="44" height="44"
        fill={face}
        opacity="0.92"
        stroke={T.ink}
        strokeWidth="0.8"
        rx="1.5"
      />
      {/* Quartering fold lines */}
      <line x1="-22" y1="0" x2="22" y2="0" stroke={T.ink} strokeWidth="0.5" opacity="0.3" />
      <line x1="0" y1="-22" x2="0" y2="22" stroke={T.ink} strokeWidth="0.5" opacity="0.3" />
      {/* Texture — small hatches suggesting microfiber weave */}
      {[-14, -7, 0, 7, 14].map((y) => (
        <line
          key={y}
          x1="-18" y1={y + 0.5}
          x2="18" y2={y + 0.5}
          stroke={T.ink}
          strokeWidth="0.3"
          opacity="0.15"
        />
      ))}
      {/* Working edge indicator */}
      <line
        x1="-22" y1="22"
        x2="22" y2="22"
        stroke={T.sienna}
        strokeWidth="2"
        opacity={isLPerim ? 1 : 0.6}
      />
    </g>
  );
}

// ─── Sleeve glyph: T-bar handle + microfiber sleeve, drips when loaded ───
function SleeveGlyph({ T, isLoading, depositing }) {
  return (
    <g>
      {/* Handle shaft */}
      <rect x="-2.5" y="-58" width="5" height="46" fill={T.ink} rx="1.5" />
      {/* T-bar — the cross at the bottom of the handle */}
      <rect x="-44" y="-14" width="88" height="6" fill={T.ink} rx="1.5" />
      {/* Sleeve body (the microfiber pad covering the T-bar) */}
      <rect
        x="-50" y="-8"
        width="100" height="16"
        fill={depositing ? T.sage : T.cloth}
        opacity="0.85"
        stroke={T.ink}
        strokeWidth="0.8"
        rx="3"
      />
      {/* Microfiber texture — fluffy edge suggested by short hatches */}
      {[-44, -32, -20, -8, 4, 16, 28, 40].map((x) => (
        <g key={x}>
          <line x1={x} y1="8" x2={x} y2="11" stroke={T.ink} strokeWidth="0.6" opacity="0.4" />
          <line x1={x + 4} y1="8" x2={x + 4} y2="10" stroke={T.ink} strokeWidth="0.5" opacity="0.3" />
        </g>
      ))}
      {/* Saturation indicator — sage tint when wet */}
      {depositing && (
        <rect
          x="-48" y="-5"
          width="96" height="11"
          fill={T.sage}
          opacity="0.35"
          rx="2"
        />
      )}
      {/* Drip beads — visible when loading or freshly loaded */}
      {(isLoading || depositing) && (
        <g>
          <ellipse cx="-30" cy="14" rx="2" ry="3" fill={T.sage} opacity="0.7" />
          <ellipse cx="-5" cy="14" rx="2" ry="3" fill={T.sage} opacity="0.7" />
          <ellipse cx="22" cy="14" rx="1.5" ry="2.4" fill={T.sage} opacity="0.6" />
        </g>
      )}
      {/* Depositing pulse */}
      {depositing && (
        <circle cx="0" cy="6" r="2" fill={T.sienna} style={{ animation: 'pulse 1.4s ease-in-out infinite' }} />
      )}
    </g>
  );
}

// ─── Sponge glyph: round natural sea sponge, irregular cells ─────────────
function SpongeGlyph({ T, pressing }) {
  return (
    <g style={pressing ? { animation: 'pinchPulse 0.6s ease-in-out' } : {}}>
      {/* Main body — irregular blob shape */}
      <path
        d="M -18 -10 Q -22 -2 -20 6 Q -16 14 -8 16 Q 0 18 8 16 Q 18 13 20 4 Q 22 -6 16 -12 Q 8 -16 0 -14 Q -10 -14 -18 -10 Z"
        fill={T.cloth}
        opacity="0.92"
        stroke={T.ink}
        strokeWidth="0.8"
      />
      {/* Cell texture — irregular pores characteristic of natural sea sponge */}
      <circle cx="-10" cy="-4" r="2" fill={T.ink} opacity="0.18" />
      <circle cx="-2" cy="2" r="1.5" fill={T.ink} opacity="0.22" />
      <circle cx="6" cy="-3" r="2.5" fill={T.ink} opacity="0.18" />
      <circle cx="12" cy="6" r="1.8" fill={T.ink} opacity="0.2" />
      <circle cx="-12" cy="6" r="1.6" fill={T.ink} opacity="0.22" />
      <circle cx="0" cy="10" r="1.4" fill={T.ink} opacity="0.18" />
      <circle cx="8" cy="-9" r="1.2" fill={T.ink} opacity="0.2" />
      <circle cx="-6" cy="-12" r="1.5" fill={T.ink} opacity="0.18" />
      {/* Wet sheen on lower contact face when pressing */}
      {pressing && (
        <path
          d="M -16 10 Q 0 18 18 10"
          fill="none"
          stroke={T.sage}
          strokeWidth="2"
          opacity="0.7"
          strokeLinecap="round"
        />
      )}
      {/* Small drip beads */}
      {pressing && (
        <>
          <ellipse cx="-8" cy="20" rx="1.5" ry="2" fill={T.sage} opacity="0.6" />
          <ellipse cx="6" cy="20" rx="1.4" ry="2" fill={T.sage} opacity="0.55" />
        </>
      )}
    </g>
  );
}

// ─── Spray bottle glyph: trigger sprayer, droplet visualization in scene ─
function SprayGlyph({ T, spraying }) {
  return (
    <g>
      {/* Bottle body */}
      <rect
        x="-12" y="-10"
        width="24" height="36"
        fill={T.surface}
        stroke={T.ink}
        strokeWidth="1.5"
        rx="2"
      />
      {/* Solution fill line */}
      <rect
        x="-10" y="-2"
        width="20" height="26"
        fill={T.sage}
        opacity="0.5"
        rx="1"
      />
      {/* Bottle neck */}
      <rect x="-6" y="-16" width="12" height="6" fill={T.ink} />
      {/* Trigger handle (back side) */}
      <path
        d="M 12 -10 L 22 -8 L 22 6 L 12 4 Z"
        fill={T.ink}
        opacity="0.85"
      />
      {/* Nozzle (front) */}
      <rect x="-22" y="-18" width="14" height="5" fill={T.ink} rx="1" />
      <line x1="-26" y1="-15.5" x2="-22" y2="-15.5" stroke={T.ink} strokeWidth="2" />
      {/* Active spray glow at nozzle */}
      {spraying && (
        <circle
          cx="-26" cy="-15.5"
          r="3"
          fill={T.sage}
          opacity="0.6"
          style={{ animation: 'pulse 0.4s ease-in-out infinite' }}
        />
      )}
      {/* Label */}
      <line x1="-9" y1="6" x2="9" y2="6" stroke={T.ink} strokeWidth="0.6" opacity="0.4" />
      <line x1="-9" y1="10" x2="6" y2="10" stroke={T.ink} strokeWidth="0.5" opacity="0.3" />
    </g>
  );
}

// ─── Pole brush head glyph: bristles + jets + pole connection ────────────
function PoleHeadGlyph({ T, bristlesActive, jetsActive }) {
  return (
    <g>
      {/* Brush head body — wide rectangle */}
      <rect
        x="-26" y="-6"
        width="52" height="10"
        fill={T.ink}
        opacity="0.9"
        stroke={T.ink}
        strokeWidth="0.5"
        rx="1.5"
      />
      {/* Pole connection — short stub on top, going up */}
      <rect
        x="-3" y="-14"
        width="6" height="8"
        fill={T.ink}
        opacity="0.85"
        rx="1"
      />
      {/* Bristles — vertical lines below the head */}
      {Array.from({ length: 18 }, (_, i) => {
        const bx = -24 + (i * 3);
        const wiggle = bristlesActive ? Math.sin(i * 0.7 + Date.now() * 0.01) * 1.2 : 0;
        return (
          <line
            key={i}
            x1={bx} y1="4"
            x2={bx + wiggle * 0.5} y2="12"
            stroke={bristlesActive ? T.sienna : T.cloth}
            strokeWidth="0.7"
            opacity={bristlesActive ? 0.85 : 0.6}
            style={{ transition: 'stroke 0.3s' }}
          />
        );
      })}
      {/* Jet ports — small holes along the front edge */}
      {[-18, -10, -2, 6, 14].map((x) => (
        <circle
          key={x}
          cx={x} cy="3"
          r="0.8"
          fill={jetsActive ? T.sage : T.ink}
          style={{ transition: 'fill 0.3s' }}
        />
      ))}
      {/* Active indicator — colored bar showing which mode is dominant */}
      <rect
        x="-26" y="-7"
        width="52" height="2"
        fill={
          bristlesActive ? T.sienna :
          jetsActive ? T.sage :
          T.inkFaint
        }
        opacity={(bristlesActive || jetsActive) ? 0.9 : 0.4}
        rx="1"
        style={{ transition: 'fill 0.3s' }}
      />
      {/* Active pulse */}
      {(bristlesActive || jetsActive) && (
        <circle
          cx="0" cy="-1"
          r="2"
          fill={bristlesActive ? T.sienna : T.sage}
          style={{ animation: 'pulse 0.8s ease-in-out infinite' }}
        />
      )}
    </g>
  );
}
